<template>
  <div class="budget-main-panel" :class="{ 'budget-main-panel--member-page': useMemberPageLayout }">
    <!-- 전체 로딩 오버레이 -->
    <div v-if="initialLoading" class="initial-loading-overlay">
      <div class="loading-spinner">
        <v-progress-circular 
          indeterminate 
          color="primary" 
          size="48"
          width="4"
        ></v-progress-circular>
        <p>예산 데이터를 불러오는 중...</p>
      </div>
    </div>

    <!-- 메인 컨텐츠 (로딩 완료 후 fade-in) -->
    <transition name="content-fade">
      <div
        v-show="!initialLoading"
        :class="[
          useMemberPageLayout ? 'page-layout' : 'budget-layout',
          { 'sidebar-right': sidebarPosition === 'right' }
        ]"
      >
        <!-- 고정 사이드바 (리모컨) -->
        <aside
          v-if="!initialLoading"
          :class="[
            useMemberPageLayout ? 'budget-remote-sidebar' : 'budget-sidebar',
            { 'budget-remote-sidebar--collapsed': useMemberPageLayout && sidebarCollapsed }
          ]"
        >
          <!-- 단독 화면: 모서리 이동. 예산추경 화면은 헤더 도구줄에 둔다 -->
          <button
            v-if="!useMemberPageLayout"
            class="sidebar-position-toggle"
            @click="toggleSidebarPosition"
            :title="sidebarPosition === 'left' ? '우측으로 이동' : '좌측으로 이동'"
          >
            <v-icon size="14">{{ sidebarPosition === 'left' ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
          </button>

          <!-- 멤버 상세(예산추경): 공통 리모컨 SCSS용 연도 바 -->
          <template v-if="useMemberPageLayout">
            <!-- 접힘 상태: 슬림 레일 (아이콘만) -->
            <div v-if="sidebarCollapsed" class="sidebar-rail">
              <button
                type="button"
                class="sidebar-rail__btn sidebar-rail__btn--expand"
                @click="toggleSidebarCollapsed"
                title="리모컨 펼치기"
              >
                <v-icon size="16">mdi-chevron-right</v-icon>
              </button>
              <span class="sidebar-rail__year">{{ searchParams.lebYear }}</span>
              <span
                v-if="laborCostBaseRevenue > 0"
                class="sidebar-rail__labor"
                :class="{
                  good: laborCompare === 'gt',
                  bad: laborCompare === 'lt',
                  balanced: laborCompare === 'eq',
                }"
                :title="`인건비 지출 비율 ${currentLaborCostRate.toFixed(1)}% (기준 ${standardLaborCostRate}%)`"
              ></span>
              <button
                type="button"
                class="sidebar-rail__btn"
                @click="openSaveDialog()"
                :disabled="saving"
                title="저장"
              >
                <v-icon size="17">mdi-content-save</v-icon>
              </button>
              <button
                type="button"
                class="sidebar-rail__btn"
                @click="openBudgetWizard"
                title="예산 마법사"
              >
                <v-icon size="17">mdi-format-list-numbered</v-icon>
              </button>
              <button
                type="button"
                class="sidebar-rail__btn sidebar-rail__btn--excel"
                @click="requestSavedExport('excel')"
                title="예산서 미리보기"
              >
                <v-icon size="17">mdi-microsoft-excel</v-icon>
              </button>
              <button
                type="button"
                class="sidebar-rail__btn"
                @click="requestSavedExport('hope')"
                title="희망이음 양식 미리보기"
              >
                <v-icon size="17">mdi-file-upload-outline</v-icon>
              </button>
              <button
                type="button"
                class="sidebar-rail__btn"
                @click="requestSavedExport('salary')"
                title="저장된 보수일람"
              >
                <v-icon size="17">mdi-account-cash-outline</v-icon>
              </button>
              <button
                v-if="showMealMirror"
                type="button"
                class="sidebar-rail__btn"
                @click="openMealMirrorDialog"
                title="식재료비·시군구보조금을 생계비로 반영"
              >
                <v-icon size="17">mdi-arrow-right-bold-outline</v-icon>
              </button>
              <div class="sidebar-rail__foot">
                <button
                  type="button"
                  class="sidebar-rail__btn"
                  @click="showPolicyDialog = true"
                  title="작성 안내"
                >
                  <v-icon size="17">mdi-scale-balance</v-icon>
                </button>
              </div>
            </div>

            <!-- 펼침 상태: 기존 리모컨 -->
            <template v-else>
            <div class="sidebar-head">
              <div class="sidebar-head__top">
                <span class="sidebar-head__caption">
                  <v-icon size="13">mdi-chart-box-outline</v-icon>
                  연간예산
                </span>
                <span class="sidebar-head__tools">
                  <span class="budget-remote-meta" :class="budgetClassStyle">
                    <v-icon size="11">{{ budgetClassIcon }}</v-icon>
                    <span>{{ budgetClassLabel }}</span>
                  </span>
                  <button
                    type="button"
                    class="sidebar-move-toggle"
                    @click="toggleSidebarPosition"
                    :title="sidebarPosition === 'left' ? '우측으로 이동' : '좌측으로 이동'"
                  >
                    <v-icon size="16">{{ sidebarPosition === 'left' ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
                  </button>
                  <button
                    type="button"
                    class="sidebar-collapse-toggle"
                    @click="toggleSidebarCollapsed"
                    title="리모컨 접기"
                  >
                    <v-icon size="16">mdi-chevron-left</v-icon>
                  </button>
                </span>
              </div>
              <div class="sidebar-ym-selector">
                <button
                  type="button"
                  class="ym-nav-btn"
                  @click="changeYear(-1)"
                  :disabled="parseInt(searchParams.lebYear) <= minYear"
                >
                  <v-icon size="18">mdi-chevron-left</v-icon>
                </button>
                <div class="ym-display">
                  <span class="ym-value">{{ searchParams.lebYear }}년</span>
                </div>
                <button
                  type="button"
                  class="ym-nav-btn"
                  @click="changeYear(1)"
                  :disabled="parseInt(searchParams.lebYear) >= maxYear"
                >
                  <v-icon size="18">mdi-chevron-right</v-icon>
                </button>
              </div>
            </div>

            <div v-if="budSumList" class="sidebar-summary sidebar-summary--basis">
              <div class="summary-block">
                <div class="summary-block__title">산출기초</div>
                <div class="summary-line">
                  <span class="summary-line__label">세입</span>
                  <span class="summary-line__value summary-line__value--in">₩ {{ budSumList.in_예산총액_총합 }}</span>
                </div>
                <div class="summary-line">
                  <span class="summary-line__label">세출</span>
                  <span class="summary-line__value summary-line__value--out">₩ {{ budSumList.out_예산총액_총합 }}</span>
                </div>
                <div
                  class="summary-line summary-line--diff"
                  :class="{
                    'budget-balanced': budgetDifference === 0,
                    'budget-unbalanced': budgetDifference !== 0,
                  }"
                >
                  <span class="summary-line__label">차액</span>
                  <span class="summary-line__value">₩ {{ addComma(budgetDifference) }}</span>
                </div>
              </div>

              <div v-if="totalExpense > 0 || laborCostBaseRevenue > 0" class="labor-block">
                <div class="labor-block__head">
                  <span class="labor-block__label">인건비 지출 비율</span>
                  <v-tooltip v-if="laborCostBaseRevenue > 0" location="bottom" max-width="280">
                    <template #activator="{ props: tooltipProps }">
                      <span
                        v-bind="tooltipProps"
                        class="labor-block__percent"
                        :class="{
                          'labor-percent-good': laborCompare === 'gt',
                          'labor-percent-bad': laborCompare === 'lt',
                          'labor-percent-balanced': laborCompare === 'eq',
                        }"
                      >
                        {{ currentLaborCostRate.toFixed(1) }}%
                      </span>
                    </template>
                    <div class="labor-tooltip-content">
                      <div class="tooltip-title">산출근거</div>
                      <div class="tooltip-row">
                        <span class="tooltip-label">인건비 지출</span>
                        <span class="tooltip-value">{{ formatMoney(totalLaborCost) }}원</span>
                      </div>
                      <div class="tooltip-note">(요양보호사·사회복지사 급여+보험+퇴직금)</div>
                      <div class="tooltip-row">
                        <span class="tooltip-label">수입</span>
                        <span class="tooltip-value">{{ formatMoney(laborCostBaseRevenue) }}원</span>
                      </div>
                      <div class="tooltip-note">(장기요양급여+본인부담금+가산금)</div>
                      <div class="tooltip-formula">
                        비율 = {{ formatMoney(totalLaborCost) }} ÷ {{ formatMoney(laborCostBaseRevenue) }} × 100 =
                        {{ currentLaborCostRate.toFixed(1) }}%
                      </div>
                    </div>
                  </v-tooltip>
                  <span v-else class="labor-block__percent labor-percent-muted">—</span>
                </div>
                <div v-if="laborCostBaseRevenue > 0" class="labor-block__bar">
                  <i
                    class="labor-block__bar-fill"
                    :class="{
                      'labor-percent-good': laborCompare === 'gt',
                      'labor-percent-bad': laborCompare === 'lt',
                      'labor-percent-balanced': laborCompare === 'eq',
                    }"
                    :style="{ width: laborBarPercent + '%' }"
                  ></i>
                  <em class="labor-block__bar-marker" :style="{ left: laborStandardPercent + '%' }"></em>
                </div>
                <div v-if="laborCostBaseRevenue > 0" class="labor-block__meta">
                  <span class="labor-block__standard">기준 {{ standardLaborCostRate }}%</span>
                  <span
                    class="labor-block__status"
                    :class="{ warning: isLaborCostBelowStandard }"
                  >
                    <v-icon size="11">{{ isLaborCostBelowStandard ? 'mdi-alert' : 'mdi-check-circle' }}</v-icon>
                    {{ isLaborCostBelowStandard ? '기준 미달' : '기준 충족' }}
                  </span>
                </div>
              </div>
            </div>

            <div class="sidebar-actions">
              <button
                type="button"
                class="action-btn primary"
                @click="openSaveDialog()"
                :disabled="saving"
              >
                <v-icon size="16">mdi-content-save</v-icon>
                <span>{{ saving ? '저장 중...' : '저장' }}</span>
              </button>
            </div>
            <div class="sidebar-menu">
              <button type="button" class="sidebar-menu__btn" title="질문에 답하면 세입·세출 행을 채워 줍니다. 저장은 따로 누릅니다" @click="openBudgetWizard">
                <v-icon size="16">mdi-format-list-numbered</v-icon>
                <span>예산 마법사</span>
              </button>
              <button
                type="button"
                class="sidebar-menu__btn"
                :disabled="loadingRecent"
                @click="openCopyConfirm"
              >
                <v-icon size="16">mdi-content-copy</v-icon>
                <span>{{ loadingRecent ? '복사 중...' : loadRecentLabel }}</span>
              </button>
              <button
                v-if="!hideRemoteBudgetDelete"
                type="button"
                class="sidebar-menu__btn sidebar-menu__btn--danger"
                @click="showDeleteDialog = true"
              >
                <v-icon size="16">mdi-delete-outline</v-icon>
                <span>삭제</span>
              </button>
              <span class="sidebar-menu__rule"></span>
              <button type="button" class="sidebar-menu__btn sidebar-menu__btn--excel" title="예산서를 미리 보고 받습니다" @click="requestSavedExport('excel')">
                <v-icon size="16">mdi-microsoft-excel</v-icon>
                <span>예산서 받기</span>
              </button>
              <button type="button" class="sidebar-menu__btn" title="희망이음 양식을 미리 보고 받습니다" @click="requestSavedExport('hope')">
                <v-icon size="16">mdi-file-upload-outline</v-icon>
                <span>희망이음 양식</span>
              </button>
              <button type="button" class="sidebar-menu__btn" title="저장된 급여·퇴직·사회보험 합계" @click="requestSavedExport('salary')">
                <v-icon size="16">mdi-account-cash-outline</v-icon>
                <span>보수일람</span>
              </button>
              <button v-if="showMealMirror" type="button" class="sidebar-menu__btn" title="식재료비는 생계비로, 시군구보조금은 생계비(보조금)으로 넣을지 확인합니다" @click="openMealMirrorDialog">
                <v-icon size="16">mdi-silverware-fork-knife</v-icon>
                <span>식재료비 반영</span>
              </button>
            </div>
            <div class="sidebar-foot">
              <button type="button" class="sidebar-menu__btn sidebar-menu__btn--policy" title="작성 기준, 사용 방법, 수가" @click="showPolicyDialog = true">
                <v-icon size="16">mdi-scale-balance</v-icon>
                <span>작성 안내</span>
              </button>
            </div>
            </template>
          </template>

          <!-- 단독(/m/budget)용 리모컨 -->
          <template v-else>
          <!-- 연도 선택 영역 -->
          <div class="sidebar-year-selector">
            <button 
              class="year-nav-btn" 
              @click="changeYear(-1)"
              :disabled="parseInt(searchParams.lebYear) <= minYear"
            >
              <v-icon size="18">mdi-chevron-left</v-icon>
            </button>
            <div class="year-display">
              <span class="year-value">{{ searchParams.lebYear }}</span>
              <span class="year-unit">년</span>
            </div>
            <button 
              class="year-nav-btn" 
              @click="changeYear(1)"
              :disabled="parseInt(searchParams.lebYear) >= maxYear"
            >
              <v-icon size="18">mdi-chevron-right</v-icon>
            </button>
          </div>

          <!-- 추경차수 표시 -->
          <div class="sidebar-budget-class" :class="budgetClassStyle">
            <v-icon size="12">{{ budgetClassIcon }}</v-icon>
            <span>{{ budgetClassLabel }}</span>
          </div>

          <!-- 세입/세출 필터 -->
          <div class="sidebar-filter">
            <div class="filter-header">
              <v-icon size="14" color="#64748b">mdi-filter-variant</v-icon>
              <span>필터</span>
            </div>
            <div class="filter-buttons">
              <button 
                class="filter-btn" 
                :class="{ active: selectedTrcode === 'ALL' }"
                @click="selectedTrcode = 'ALL'"
              >전체</button>
              <button 
                class="filter-btn revenue" 
                :class="{ active: selectedTrcode === 'IN' }"
                @click="selectedTrcode = 'IN'"
              >세입</button>
              <button 
                class="filter-btn expense" 
                :class="{ active: selectedTrcode === 'OUT' }"
                @click="selectedTrcode = 'OUT'"
              >세출</button>
            </div>
          </div>

          <!-- 세입/세출 요약 (타 시스템 budSumList·차액·인건비 비교와 동일 구조) -->
          <div v-if="budSumList" class="sidebar-summary sidebar-summary--basis">
            <table class="budget-summary-table">
              <tbody>
                <tr>
                  <td colspan="2" class="budget-summary-table__section-title">
                    <span class="budget-summary-table__title">산출기초</span>
                  </td>
                </tr>
                <tr>
                  <td class="budget-summary-table__label">세입</td>
                  <td class="budget-summary-table__value budget-summary-table__value--in">
                    ₩ {{ budSumList.in_예산총액_총합 }}
                  </td>
                </tr>
                <tr>
                  <td class="budget-summary-table__label">세출</td>
                  <td class="budget-summary-table__value budget-summary-table__value--out">
                    ₩ {{ budSumList.out_예산총액_총합 }}
                  </td>
                </tr>
                <tr>
                  <td
                    class="budget-summary-table__label budget-difference"
                    :class="{
                      'budget-balanced': budgetDifference === 0,
                      'budget-unbalanced': budgetDifference !== 0,
                    }"
                  >
                    차액
                  </td>
                  <td
                    class="budget-summary-table__value budget-difference"
                    :class="{
                      'budget-balanced': budgetDifference === 0,
                      'budget-unbalanced': budgetDifference !== 0,
                    }"
                  >
                    ₩ {{ addComma(budgetDifference) }}
                  </td>
                </tr>
                <tr v-if="totalExpense > 0 || laborCostBaseRevenue > 0" class="budget-summary-table__labor-row">
                  <td colspan="2" class="budget-summary-table__labor-cell">
                    <div class="budget-summary-labor-block">
                      <span class="budget-summary-labor-label">인건비 지출 비율</span>
                      <v-tooltip v-if="laborCostBaseRevenue > 0" location="bottom" max-width="280">
                        <template #activator="{ props: tooltipProps }">
                          <span v-bind="tooltipProps" class="budget-summary-labor-activator">
                            <span
                              class="labor-percent"
                              :class="{
                                'labor-percent-good': laborCompare === 'gt',
                                'labor-percent-bad': laborCompare === 'lt',
                                'labor-percent-balanced': laborCompare === 'eq',
                              }"
                            >
                              {{ currentLaborCostRate.toFixed(1) }}%
                            </span>
                            <span v-if="laborCompare === 'gt'" class="percent-comparison percent-greater">
                              &gt; {{ standardLaborCostRate }}%
                            </span>
                            <span v-else-if="laborCompare === 'lt'" class="percent-comparison percent-less">
                              &lt; {{ standardLaborCostRate }}%
                            </span>
                            <span v-else-if="laborCompare === 'eq'" class="percent-comparison percent-equal">
                              = {{ standardLaborCostRate }}%
                            </span>
                          </span>
                        </template>
                        <div class="labor-tooltip-content">
                          <div class="tooltip-title">산출근거</div>
                          <div class="tooltip-row">
                            <span class="tooltip-label">인건비 지출</span>
                            <span class="tooltip-value">{{ formatMoney(totalLaborCost) }}원</span>
                          </div>
                          <div class="tooltip-note">(요양보호사·사회복지사 급여+보험+퇴직금)</div>
                          <div class="tooltip-row">
                            <span class="tooltip-label">수입</span>
                            <span class="tooltip-value">{{ formatMoney(laborCostBaseRevenue) }}원</span>
                          </div>
                          <div class="tooltip-note">(장기요양급여+본인부담금+가산금)</div>
                          <div class="tooltip-formula">
                            비율 = {{ formatMoney(totalLaborCost) }} ÷ {{ formatMoney(laborCostBaseRevenue) }} × 100 =
                            {{ currentLaborCostRate.toFixed(1) }}%
                          </div>
                        </div>
                      </v-tooltip>
                      <span v-else class="labor-percent labor-percent-muted">—</span>
                      <div
                        v-if="laborCostBaseRevenue > 0"
                        class="labor-cost-status budget-summary-labor-status"
                        :class="{ warning: isLaborCostBelowStandard }"
                      >
                        <v-icon size="12" :color="isLaborCostBelowStandard ? '#ef4444' : '#10b981'">
                          {{ isLaborCostBelowStandard ? 'mdi-alert' : 'mdi-check-circle' }}
                        </v-icon>
                        <span v-if="isLaborCostBelowStandard">기준 미달</span>
                        <span v-else>기준 충족</span>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="sidebar-actions">
            <button
              class="action-btn save"
              @click="openSaveDialog()"
              :disabled="saving"
            >
              <v-icon size="16">mdi-content-save</v-icon>
              <span>{{ saving ? '저장 중...' : '저장' }}</span>
            </button>
          </div>
          <div class="sidebar-menu">
            <button type="button" class="sidebar-menu__btn" @click="openBudgetWizard">
              <v-icon size="16">mdi-format-list-numbered</v-icon>
              <span>예산 마법사</span>
            </button>
            <button
              v-if="!hideRemoteBudgetDelete"
              type="button"
              class="sidebar-menu__btn sidebar-menu__btn--danger"
              @click="showDeleteDialog = true"
            >
              <v-icon size="16">mdi-delete-outline</v-icon>
              <span>삭제</span>
            </button>
          </div>
          <div class="sidebar-foot">
            <button type="button" class="sidebar-menu__btn sidebar-menu__btn--policy" title="작성 기준, 사용 방법, 수가" @click="showPolicyDialog = true">
              <v-icon size="16">mdi-scale-balance</v-icon>
              <span>작성 안내</span>
            </button>
          </div>

          </template>
        </aside>

      <!-- 우측 메인 컨텐츠 -->
      <main :class="useMemberPageLayout ? 'page-main' : 'budget-main-content'">
        <div v-if="useMemberPageLayout" class="budget-topbar">
          <div class="budget-view-tabs" role="tablist" aria-label="세입·세출 보기 전환">
            <button
              type="button"
              role="tab"
              class="budget-view-tab budget-view-tab--all"
              :class="{ active: selectedTrcode === 'ALL' }"
              :aria-selected="selectedTrcode === 'ALL'"
              @click="selectedTrcode = 'ALL'"
            >전체</button>
            <button
              type="button"
              role="tab"
              class="budget-view-tab budget-view-tab--in"
              :class="{ active: selectedTrcode === 'IN' }"
              :aria-selected="selectedTrcode === 'IN'"
              @click="selectedTrcode = 'IN'"
            >세입</button>
            <button
              type="button"
              role="tab"
              class="budget-view-tab budget-view-tab--out"
              :class="{ active: selectedTrcode === 'OUT' }"
              :aria-selected="selectedTrcode === 'OUT'"
              @click="selectedTrcode = 'OUT'"
            >세출</button>
          </div>
          <slot name="toolbar" />
        </div>
        <div :class="useMemberPageLayout ? 'content-card' : 'budget-table-outer'">
        <v-card class="table-card">
          <v-card-text class="table-card-text">
        <!-- 로딩 -->
        <div v-if="loading" class="loading-container">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p>데이터를 불러오는 중입니다...</p>
        </div>

        <!-- 재무코드 테이블 -->
        <div v-else-if="processedData.length > 0" class="budget-table-with-hint">
          <div class="finance-table-wrapper">
          <table class="finance-table">
            <thead>
              <tr>
                <th>구분</th>
                <th v-if="showCodeColumns">관</th>
                <th v-if="showCodeColumns">항</th>
                <th class="scode-head">
                  <div class="scode-head__inner">
                    <span class="scode-head__name">목</span>
                    <button
                      type="button"
                      class="code-col-toggle"
                      :class="{ 'is-open': showCodeColumns }"
                      @click="showCodeColumns = !showCodeColumns"
                    >
                      {{ showCodeColumns ? '관·항 접기' : '관·항 펼치기' }}
                    </button>
                  </div>
                </th>
                <th>내용</th>
                <th>단가</th>
                <th>명(회)</th>
                <th>개월</th>
                <th>금액</th>
                <th>자금원천</th>
                <th>기능</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(item, index) in processedData" :key="index">
                <tr
                  :class="{
                    'row-group-s': item.showScode,
                    'row-group-m': item.showMcode,
                    'row-group-l': item.showLcode,
                    'row-group-t': item.showTrcode,
                  }"
                >
                  <!-- 구분 (세입/세출) - rowspan 적용 -->
                  <td 
                    v-if="item.showTrcode"
                    :rowspan="item.trcodeRowspan"
                    :class="['trcode-cell', item.fc_trcode === 'IN' ? 'income' : 'expense']"
                  >
                    {{ item.fc_trname }}
                  </td>

                  <!-- 관 (L코드) - rowspan 적용 -->
                  <td 
                    v-if="showCodeColumns && item.showLcode"
                    :rowspan="item.lcodeRowspan"
                    :class="['lcode-cell', item.fc_trcode === 'IN' ? 'income' : 'expense']"
                  >
                    <div class="name-label">{{ item.fc_lname }}</div>
                    <div class="budget-sum" :class="item.fc_trcode === 'IN' ? 'is-in' : 'is-out'" v-if="getLcodeBudgetSum(item) > 0">
                      {{ formatMoney(getLcodeBudgetSum(item)) }}원
                    </div>
                  </td>

                  <!-- 항 (M코드) - rowspan 적용 -->
                  <td 
                    v-if="showCodeColumns && item.showMcode"
                    :rowspan="item.mcodeRowspan"
                    class="mcode-cell"
                  >
                    <div class="name-label">{{ item.fc_mname }}</div>
                    <div class="budget-sum" :class="item.fc_trcode === 'IN' ? 'is-in' : 'is-out'" v-if="getMcodeBudgetSum(item) > 0">
                      {{ formatMoney(getMcodeBudgetSum(item)) }}원
                    </div>
                  </td>

                  <!-- 목 (S코드) - rowspan 적용 -->
                  <td 
                    v-if="item.showScode"
                    :rowspan="item.scodeRowspan"
                    class="scode-cell"
                  >
                  <div class="name-label" title="두 번 누르면 내용에 목 이름이 들어갑니다" @dblclick.stop="copySnameToDescription(item)">{{ item.fc_sname }}</div>
                  <div class="budget-cell" :class="item.fc_trcode === 'IN' ? 'is-in' : 'is-out'" v-if="getScodeBudgetSum(item) > 0">{{ formatMoney(getScodeBudgetSum(item)) }}원</div>

                  </td>

                  <!-- 내용 -->
                  <td class="description-cell">
                    <!-- 급여 항목인 경우 셀렉트 리스트 -->
                    <select
                      v-if="item.isSalaryItem"
                      v-model="item.description"
                      class="salary-select"
                      :class="{ 'linked-highlight': isLinkedHighlighted(item) }"
                      @change="handleSalaryTypeChange(item)"
                      @focus="handleSalaryFocus(item)"
                      @blur="handleSalaryBlur()"
                    >
                      <option 
                        v-for="opt in getSalaryOptions(item.fc_scode)" 
                        :key="opt.value" 
                        :value="opt.value"
                      >{{ opt.label }}</option>
                    </select>
                    <!-- 본인부담금/장기요양급여 항목인 경우 수가 선택 + 횟수 -->
                    <div v-else-if="isRevenuePairItem(item)" class="suga-calc-wrapper">
                      <select
                        v-model="item.suga_rank"
                        class="suga-select"
                        :class="{ 'revenue-pair-highlight': isRevenuePairHighlighted(item) }"
                        @change="handleSugaChange(item)"
                        @focus="handleRevenuePairFocus(item)"
                        @blur="handleRevenuePairBlur()"
                      >
                        <option disabled value="">{{ effectiveSugaList.length ? '수가 구간 선택' : '수가 목록 로딩…' }}</option>
                        <option
                          v-for="suga in effectiveSugaList"
                          :key="suga.suga_idx + '-' + suga.suga_rank"
                          :value="suga.suga_rank"
                          :title="`급여 ${formatMoney(suga.suga_money)} / 본인부담 ${formatMoney(suga.suga_bonin)}`"
                        >{{ suga.suga_rank }}</option>
                      </select>
                      <span class="suga-multiply">×</span>
                      <input 
                        type="text" 
                        v-model="item.suga_times"
                        class="suga-times-input"
                        :class="{ 'revenue-pair-highlight': isRevenuePairHighlighted(item) }"
                        @input="handleSugaTimesInput($event, item)"
                        @focus="handleRevenuePairFocus(item)"
                        @blur="handleRevenuePairBlur()"
                      />
                    </div>
                    <!-- 일반 항목인 경우 인풋 -->
                    <input 
                      v-else
                      type="text" 
                      v-model="item.description" 
                      class="description-input"
                      :disabled="item.isLinkedItem"
                      :class="{ 
                        'readonly-input': item.isLinkedItem,
                        'linked-highlight': isLinkedHighlighted(item)
                      }"
                    />
                  </td>

                  <!-- 단가 -->
                  <td class="unit-price-cell">
                    <input 
                      type="text" 
                      v-model="item.unit_price" 
                      class="number-input"
                      :disabled="isLinkedReadonly(item) || isRevenuePairItem(item)"
                      :class="{ 
                        'readonly-input': isLinkedReadonly(item) || isRevenuePairItem(item),
                        'linked-highlight': isLinkedHighlighted(item),
                        'revenue-pair-highlight': isRevenuePairHighlighted(item),
                        'manual-edit-input': item.isLinkedItem && item.hb_pay_rel_manual === 'Y'
                      }"
                      @input="item.isSalaryItem ? handleSalaryInput($event, item, 'unit_price') : formatNumber($event, item, 'unit_price')"
                      @focus="item.isSalaryItem ? handleSalaryFocus(item) : (isRevenuePairItem(item) && handleRevenuePairFocus(item))"
                      @blur="item.isSalaryItem ? handleSalaryBlur() : (isRevenuePairItem(item) && handleRevenuePairBlur())"
                    />
                  </td>

                  <!-- 명(회) -->
                  <td class="count-cell">
                    <input 
                      type="text" 
                      v-model="item.count" 
                      class="number-input"
                      :disabled="isLinkedReadonly(item)"
                      :class="{ 
                        'readonly-input': isLinkedReadonly(item),
                        'linked-highlight': isLinkedHighlighted(item),
                        'revenue-pair-highlight': isRevenuePairHighlighted(item),
                        'manual-edit-input': item.isLinkedItem && item.hb_pay_rel_manual === 'Y'
                      }"
                      @input="item.isSalaryItem ? handleSalaryInput($event, item, 'count') : (isRevenuePairItem(item) ? handleRevenuePairInput($event, item, 'count') : formatNumber($event, item, 'count'))"
                      @focus="item.isSalaryItem ? handleSalaryFocus(item) : (isRevenuePairItem(item) && handleRevenuePairFocus(item))"
                      @blur="item.isSalaryItem ? handleSalaryBlur() : (isRevenuePairItem(item) && handleRevenuePairBlur())"
                    />
                  </td>

                  <!-- 개월 -->
                  <td class="month-cell">
                    <input 
                      type="text" 
                      v-model="item.months" 
                      class="number-input"
                      :disabled="isLinkedReadonly(item)"
                      :class="{ 
                        'readonly-input': isLinkedReadonly(item),
                        'linked-highlight': isLinkedHighlighted(item),
                        'revenue-pair-highlight': isRevenuePairHighlighted(item),
                        'manual-edit-input': item.isLinkedItem && item.hb_pay_rel_manual === 'Y'
                      }"
                      @input="item.isSalaryItem ? handleSalaryInput($event, item, 'months') : (isRevenuePairItem(item) ? handleRevenuePairInput($event, item, 'months') : formatNumber($event, item, 'months'))"
                      @focus="item.isSalaryItem ? handleSalaryFocus(item) : (isRevenuePairItem(item) && handleRevenuePairFocus(item))"
                      @blur="item.isSalaryItem ? handleSalaryBlur() : (isRevenuePairItem(item) && handleRevenuePairBlur())"
                    />
                  </td>

                  <!-- 금액 (자동계산) -->
                  <td class="amount-cell">
                    <v-tooltip 
                      v-if="isLinkedCeoItem(item)"
                      location="top"
                    >
                      <template v-slot:activator="{ props }">
                        <span v-bind="props" style="display: inline-flex; align-items: center; gap: 4px; cursor: help;">
                          {{ calculateAmount(item) }}
                          <v-icon size="14" color="info">mdi-information-outline</v-icon>
                        </span>
                      </template>
                      <div style="padding: 4px 0;">
                        <div style="font-weight: bold; margin-bottom: 6px;">대표자 사회보험 안내</div>
                        <div style="font-size: 12px; line-height: 1.6;">
                          <div v-for="note in ceoInsuranceNotes(item)" :key="note">- {{ note }}</div>
                        </div>
                      </div>
                    </v-tooltip>
                    <span v-else>{{ calculateAmount(item) }}</span>
                  </td>

                  <!-- 자금원천 (코드 → 자부담·후원금 등 표시명) -->
                  <td class="funding-cell">
                    {{ fundingCellLabel(item) }}
                  </td>

                  <!-- 기능 -->
                  <td class="function-cell">
                    <!-- 연결된 항목 (사회보험/퇴직금) -->
                    <div v-if="item.isLinkedItem" class="linked-buttons">
                      <v-switch
                        :model-value="item.hb_pay_rel_manual === 'Y'"
                        @update:model-value="toggleManualEdit(item)"
                        density="compact"
                        hide-details
                        color="warning"
                        class="manual-switch"
                      >
                        <template v-slot:label>
                          <span class="switch-label">{{ item.hb_pay_rel_manual === 'Y' ? '수동' : '자동' }}</span>
                        </template>
                      </v-switch>
                    </div>
                    <!-- 일반 항목 (급여 포함) -->
                    <div v-else class="function-buttons">
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click.stop="addRow(index)"
                        class="add-btn"
                      >
                        <v-icon size="16">mdi-plus</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        color="error"
                        @click.stop="deleteRow(index)"
                        :disabled="!canDeleteRow(item)"
                        class="delete-btn"
                      >
                        <v-icon size="16">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        </div>

        <div v-else class="no-data-container">
          <v-icon size="48" color="grey">mdi-database-alert</v-icon>
          <p>해당 연도 재무코드가 없거나 표시할 행이 없습니다.</p>
        </div>
          </v-card-text>
        </v-card>
        </div>
      </main>
      </div>
    </transition>

    <!-- 예산 삭제 확인 다이얼로그 (전체 예산 삭제) -->
    <v-dialog v-model="showDeleteDialog" class="budget-page-dialog" max-width="450px">
      <v-card class="delete-dialog-card">
        <v-card-title class="delete-dialog-header">
          <v-icon size="24" color="error" class="mr-2">mdi-alert-circle</v-icon>
          <span>예산 삭제 확인</span>
          <button type="button" class="dialog-close-x" title="닫기" @click="showDeleteDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="delete-dialog-content">
          <p v-if="deleteDialogChecking" class="delete-dialog-checking">차수 정보를 확인하는 중입니다…</p>
          <template v-else>
            <p class="delete-warning-text">
              <strong>{{ searchParams.lebYear }}년 {{ budgetClassLabel }}</strong>에 저장된 예산(및 같은 차수의 추경 내역)을 모두 삭제합니다. 계속하시겠습니까?
            </p>
            <p class="delete-order-tip">
              <v-icon size="16" color="#1565c0" class="delete-order-tip__icon">mdi-information-outline</v-icon>
              <span>2차·3차 추경이 모두 있을 때는 <strong>3차 → 2차 → 본예산</strong> 순으로만 삭제할 수 있습니다. 가장 큰 차수부터 진행해 주세요.</span>
            </p>
            <p v-if="deleteDialogBlocked" class="delete-dialog-blocked" role="alert">
              <v-icon size="18" color="#c62828" class="delete-dialog-blocked__icon">mdi-lock-alert</v-icon>
              {{ deleteDialogBlocked }}
            </p>
            <p class="delete-notice">
              삭제된 데이터는 복구할 수 없습니다.
            </p>
          </template>
        </v-card-text>
        <v-card-actions class="delete-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            class="btn-cancel"
            @click="showDeleteDialog = false"
          >
            취소
          </v-btn>
          <v-btn
            variant="flat"
            class="btn-delete"
            @click="deleteBudget"
            :loading="deleting"
            :disabled="deleteDialogChecking || Boolean(deleteDialogBlocked)"
          >
            <v-icon size="18">mdi-delete</v-icon>
            <span class="ml-1">삭제</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 저장 확인 다이얼로그 -->
    <v-dialog v-model="showSaveConfirmDialog" class="budget-page-dialog" max-width="400px">
      <v-card class="save-confirm-dialog-card">
        <v-card-title class="save-confirm-header">
          <v-icon size="24" color="primary" class="mr-2">mdi-content-save</v-icon>
          <span>저장 확인</span>
          <button type="button" class="dialog-close-x" title="닫기" @click="showSaveConfirmDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="save-confirm-content">
          <p v-if="saveConfirmNote" class="save-confirm-note">{{ saveConfirmNote }}</p>
          <p>{{ searchParams.lebYear }}년 {{ budgetClassLabel }} 예산을 저장하시겠습니까?</p>
          <p v-if="pendingDeleteIds.length > 0" class="delete-notice save-confirm-pending-del">
            <v-icon size="14" color="warning" class="save-confirm-pending-del__icon">mdi-alert</v-icon>
            삭제 대기 중인 항목 {{ pendingDeleteIds.length }}개가 함께 삭제됩니다.
          </p>
        </v-card-text>
        <v-card-actions class="save-confirm-actions">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="showSaveConfirmDialog = false"
          >
            취소
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="confirmSave"
            :loading="saving"
          >
            <v-icon size="18">mdi-check</v-icon>
            <span class="ml-1">저장</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showLeaveDialog" class="budget-page-dialog" max-width="440px" persistent>
      <v-card class="save-confirm-dialog-card">
        <v-card-title class="save-confirm-header">
          <v-icon size="22" color="#1e3a5f" class="mr-2">mdi-content-save-alert</v-icon>
          <span>저장하지 않은 예산</span>
          <button type="button" class="dialog-close-x" title="닫기" :disabled="leavingSave" @click="cancelLeave">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="save-confirm-content">
          <p>저장하지 않은 내용이 있습니다. 저장하지 않으면 이 화면을 떠날 때 사라집니다.</p>
        </v-card-text>
        <v-card-actions class="save-confirm-actions budget-leave-actions">
          <v-btn variant="outlined" :disabled="leavingSave" @click="cancelLeave">머무르기</v-btn>
          <v-spacer />
          <v-btn variant="outlined" :disabled="leavingSave" @click="leaveWithoutSave">그냥 나가기</v-btn>
          <v-btn color="primary" variant="elevated" :loading="leavingSave" @click="leaveAfterSave">저장하고 나가기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCopyConfirmDialog" class="budget-page-dialog" max-width="480px">
      <v-card class="save-confirm-dialog-card">
        <v-card-title class="save-confirm-header">
          <v-icon size="22" color="#1e3a5f" class="mr-2">mdi-content-copy</v-icon>
          <span>{{ loadRecentLabel }}</span>
          <button type="button" class="dialog-close-x" title="닫기" @click="showCopyConfirmDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="save-confirm-content">
          <template v-if="copyConfirmIsMain">
            <p>전년도 최고 차수를 이번 본예산으로 가져옵니다. 지금 본예산은 지워집니다.</p>
            <ul class="copy-confirm-list">
              <li>급여는 전년도 금액 그대로 둡니다.</li>
              <li>퇴직·사회보험은 올해 요율로 다시 계산합니다.</li>
              <li>본인부담금·장기요양급여수입은 가져오지 않습니다.</li>
            </ul>
          </template>
          <p v-else>직전 차수에 적힌 항목을 이 시트에 그대로 덮어씁니다. 보험·식재료비·수가를 새로 만들어 넣지 않습니다. 이 시트의 기존 내용은 지워집니다.</p>
        </v-card-text>
        <v-card-actions class="save-confirm-actions">
          <v-spacer />
          <v-btn variant="outlined" :disabled="loadingRecent" @click="showCopyConfirmDialog = false">취소</v-btn>
          <v-btn color="primary" variant="elevated" :loading="loadingRecent" @click="runCopyRecent">복사</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showBudgetSheet" max-width="1180" class="budget-sheet-dialog budget-page-dialog">
      <v-card class="budget-sheet">
        <header class="budget-sheet__head">
          <div>
            <p class="budget-sheet__kicker">내려받기 전에 확인</p>
            <h2 class="budget-sheet__title">예산서</h2>
          </div>
          <button type="button" class="dialog-close-x" title="닫기" @click="showBudgetSheet = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>
        <v-card-text class="budget-sheet__body">
          <article class="budget-sheet__paper">
            <h3 class="budget-sheet__doc-title">세입·세출 예산서</h3>
            <p class="budget-sheet__doc-sub">{{ searchParams.lebYear }}년 {{ props.bizGubun || "사업" }} · {{ budgetClassLabel }}</p>
            <p v-if="!budgetSheetLines.length" class="budget-sheet__empty">표시할 금액이 없습니다.</p>
            <div v-else class="budget-sheet__scroll">
              <table class="budget-sheet__table">
                <thead>
                  <tr>
                    <th>구분</th>
                    <th>관</th>
                    <th>항</th>
                    <th>목</th>
                    <th class="num">보조금</th>
                    <th class="num">자부담</th>
                    <th class="num">후원금</th>
                    <th class="num">수익사업</th>
                    <th class="num">예산총액</th>
                    <th>항목</th>
                    <th>산출기초</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in budgetSheetLines" :key="row.key" :class="{ 'is-spacer': row.spacer }">
                    <td v-if="row.spacer" colspan="11"></td>
                    <template v-else>
                    <td v-if="row.gubun.span > 0" :rowspan="row.gubun.span" class="side" :class="[row.side === 'in' ? 'sheet-in' : 'sheet-out', { 'is-total': row.totalRow }]">{{ row.gubun.text }}</td>
                    <td v-if="row.lname.span > 0" :rowspan="row.lname.span" class="band" :class="[row.side === 'in' ? 'sheet-in' : 'sheet-out', { 'is-total': row.totalRow }]">{{ row.lname.text }}</td>
                    <td v-if="row.mname.span > 0" :rowspan="row.mname.span" class="band" :class="[row.side === 'in' ? 'sheet-in' : 'sheet-out', { 'is-total': row.totalRow }]">{{ row.mname.text }}</td>
                    <td class="mok band" :class="[row.side === 'in' ? 'sheet-in' : 'sheet-out', { 'is-total': row.totalRow }]">{{ row.sname }}</td>
                    <td class="num" :class="{ 'is-total': row.totalRow }">{{ addComma(row.subsidy) }}</td>
                    <td class="num" :class="{ 'is-total': row.totalRow }">{{ addComma(row.selfPay) }}</td>
                    <td class="num" :class="{ 'is-total': row.totalRow }">{{ addComma(row.donate) }}</td>
                    <td class="num" :class="{ 'is-total': row.totalRow }">{{ addComma(row.profit) }}</td>
                    <td class="num total" :class="{ 'is-total': row.totalRow }">{{ addComma(row.total) }}</td>
                    <td class="memo">{{ row.item }}</td>
                    <td class="memo">{{ row.basis }}</td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="budgetDirty" class="budget-sheet__warn">지금 화면의 예산서입니다. 저장하지 않은 수정은 파일에 들어가지 않습니다.</p>
          </article>
        </v-card-text>
        <v-card-actions class="budget-sheet__actions">
          <v-spacer />
          <v-btn variant="text" class="btn-close" :disabled="saving" @click="showBudgetSheet = false">닫기</v-btn>
          <template v-if="budgetDirty">
            <v-btn variant="outlined" :disabled="saving" @click="downloadSheetSaved">저장된 내용으로</v-btn>
            <v-btn color="primary" variant="elevated" :loading="saving" @click="downloadSheetAfterSave">저장 후 받기</v-btn>
          </template>
          <v-btn v-else color="primary" variant="elevated" :disabled="saving" @click="downloadSheetSaved">예산서 받기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showHopeSheet" max-width="1180" class="budget-sheet-dialog budget-page-dialog">
      <v-card class="budget-sheet">
        <header class="budget-sheet__head">
          <div>
            <p class="budget-sheet__kicker">내려받기 전에 확인</p>
            <h2 class="budget-sheet__title">희망이음 양식</h2>
          </div>
          <button type="button" class="dialog-close-x" title="닫기" @click="showHopeSheet = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>
        <v-card-text class="budget-sheet__body">
          <article class="budget-sheet__paper">
            <h3 class="budget-sheet__doc-title">예산업로드</h3>
            <p class="budget-sheet__doc-sub">{{ searchParams.lebYear }}년 {{ props.bizGubun || "사업" }} · {{ budgetClassLabel }}</p>
            <p v-if="!hopeSheetLines.length" class="budget-sheet__empty">표시할 금액이 없습니다.</p>
            <div v-else class="budget-sheet__scroll">
              <table class="budget-sheet__table budget-sheet__table--hope">
                <thead>
                  <tr>
                    <th>사업코드</th>
                    <th>사업명</th>
                    <th>계정코드(세목)</th>
                    <th>계정명(세목)</th>
                    <th>자금원천코드</th>
                    <th>자금원천</th>
                    <th class="num">예산액</th>
                    <th>국비</th>
                    <th>시도비</th>
                    <th>시군구비</th>
                    <th>산출근거</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in hopeSheetLines" :key="row.key">
                    <td class="code">{{ row.bizCode }}</td>
                    <td>{{ row.bizName }}</td>
                    <td class="code">{{ row.accountCode }}</td>
                    <td class="name">{{ row.accountName }}</td>
                    <td class="code">{{ row.fundCode }}</td>
                    <td class="code">{{ row.fundName }}</td>
                    <td class="num">{{ addComma(row.amount) }}</td>
                    <td class="code">{{ row.national }}</td>
                    <td class="code">{{ row.sido }}</td>
                    <td class="code">{{ row.sigungu }}</td>
                    <td class="memo">{{ row.basis }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="budgetDirty" class="budget-sheet__warn">지금 화면의 양식입니다. 저장하지 않은 수정은 파일에 들어가지 않습니다.</p>
          </article>
        </v-card-text>
        <v-card-actions class="budget-sheet__actions">
          <v-spacer />
          <v-btn variant="text" class="btn-close" :disabled="saving" @click="showHopeSheet = false">닫기</v-btn>
          <template v-if="budgetDirty">
            <v-btn variant="outlined" :disabled="saving" @click="downloadHopeSaved">저장된 내용으로</v-btn>
            <v-btn color="primary" variant="elevated" :loading="saving" @click="downloadHopeAfterSave">저장 후 받기</v-btn>
          </template>
          <v-btn v-else color="primary" variant="elevated" :disabled="saving" @click="downloadHopeSaved">양식 받기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSavedExportDialog" class="budget-page-dialog" max-width="440px">
      <v-card class="save-confirm-dialog-card">
        <v-card-title class="save-confirm-header">
          <v-icon size="22" color="#1e3a5f" class="mr-2">mdi-content-save-alert</v-icon>
          <span>{{ savedExportTitle }}</span>
          <button type="button" class="dialog-close-x" title="닫기" @click="showSavedExportDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="save-confirm-content">
          <p>이 자료는 마지막으로 저장된 예산으로 만듭니다. 화면에 저장하지 않은 수정은 빠집니다.</p>
        </v-card-text>
        <v-card-actions class="save-confirm-actions budget-export-actions">
          <v-spacer />
          <v-btn variant="text" :disabled="saving" @click="showSavedExportDialog = false">취소</v-btn>
          <v-btn variant="outlined" :disabled="saving" @click="exportSavedAnyway">저장된 내용으로</v-btn>
          <v-btn color="primary" variant="elevated" :loading="saving" @click="saveThenExport">저장 후 진행</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPolicyDialog" max-width="800" scrollable class="budget-policy-dialog budget-page-dialog">
      <v-card class="budget-policy">
        <header class="budget-policy__head">
          <div>
            <p class="budget-policy__kicker">예산</p>
            <h2 class="budget-policy__title">작성 안내</h2>
          </div>
          <button type="button" class="dialog-close-x" title="닫기" @click="showPolicyDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>
        <v-card-text class="budget-policy__body">
          <section class="budget-policy__block">
            <header class="budget-policy__block-head">
              <h3 class="budget-policy__group">작성 기준</h3>
            </header>
            <ol class="budget-policy__list">
              <li v-for="(item, index) in fixedBudgetPolicy" :key="item.title" class="budget-policy__item">
                <span class="budget-policy__num">{{ String(index + 1).padStart(2, "0") }}</span>
                <div class="budget-policy__copy">
                  <h3 class="budget-policy__name">{{ item.title }}</h3>
                  <p v-for="(paragraph, paragraphIndex) in item.paragraphs" :key="paragraphIndex" class="budget-policy__text">
                    {{ paragraph }}
                  </p>
                </div>
              </li>
            </ol>
          </section>

          <section class="budget-policy__block">
            <header class="budget-policy__block-head">
              <h3 class="budget-policy__group">사용 안내</h3>
            </header>
            <ol class="budget-policy__list">
              <li v-for="(item, index) in budgetGuide" :key="item.title" class="budget-policy__item">
                <span class="budget-policy__num">{{ String(index + 1).padStart(2, "0") }}</span>
                <div class="budget-policy__copy">
                  <h3 class="budget-policy__name">{{ item.title }}</h3>
                  <p v-for="(paragraph, paragraphIndex) in item.paragraphs" :key="paragraphIndex" class="budget-policy__text">
                    {{ paragraph }}
                  </p>
                </div>
              </li>
            </ol>
          </section>

          <section class="budget-policy__block">
            <header class="budget-policy__block-head">
              <h3 class="budget-policy__group">적용 요율</h3>
            </header>
            <p class="budget-policy__note">{{ searchParams.lebYear }}년 · {{ taxRatesSourceLabel }}. 급여에 연결된 사회보험과 퇴직적립금 자동 계산에 씁니다.</p>
            <table v-if="effectiveTaxData && taxRatesTableRows.length" class="budget-policy__rates">
              <tbody>
                <tr v-for="row in taxRatesTableRows" :key="row.label">
                  <th scope="row">
                    <span>{{ row.label }}</span>
                    <span v-if="row.sub" class="budget-policy__rate-sub">{{ row.sub }}</span>
                  </th>
                  <td>{{ row.value }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else class="budget-policy__note">이 연도에 맞는 요율을 불러오지 못했습니다.</p>
          </section>

          <section class="budget-policy__block">
            <header class="budget-policy__block-head">
              <div>
                <h3 class="budget-policy__group">수가</h3>
                <p class="budget-policy__block-year">{{ searchParams.lebYear }}년 {{ props.bizGubun || "사업" }}</p>
              </div>
              <div class="budget-policy__block-meta">
                <button v-if="isAdmin" type="button" class="budget-policy__edit" @click="openSugaPolicyEdit">수정</button>
                <span v-else class="budget-policy__lock-note">관리자만 수정</span>
              </div>
            </header>
            <div v-if="sugaPolicyItem" class="budget-policy__copy">
              <p v-for="(paragraph, paragraphIndex) in sugaPolicyItem.paragraphs" :key="paragraphIndex" class="budget-policy__text">
                {{ paragraph }}
              </p>
            </div>
            <div class="budget-policy__suga-wrap">
              <table v-if="effectiveSugaList.length" class="budget-policy__suga">
                <thead>
                  <tr>
                    <th>구간</th>
                    <th class="num">급여총액</th>
                    <th class="num">본인부담</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="suga in effectiveSugaList" :key="suga.suga_idx + '-' + suga.suga_rank">
                    <td>{{ suga.suga_rank }}</td>
                    <td class="num">{{ formatMoney(suga.suga_money) }}</td>
                    <td class="num">{{ formatMoney(suga.suga_bonin) }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="budget-policy__note">이 연도, 이 사업의 수가가 없습니다.</p>
            </div>
          </section>
        </v-card-text>
        <v-card-actions class="budget-policy__actions">
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="showPolicyDialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showMealMirrorDialog" class="budget-page-dialog" max-width="560">
      <v-card class="meal-mirror-dialog">
        <v-card-title class="meal-mirror-dialog__title">
          식재료비 반영
          <button type="button" class="dialog-close-x" title="닫기" @click="showMealMirrorDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="meal-mirror-dialog__body">
          <p class="meal-mirror-dialog__lead">세입 식재료비와 같은 금액을 세출 생계비로, 시군구보조금과 같은 금액을 생계비(보조금)으로 넣습니다. 이미 연결된 줄이 있으면 그 금액이 바뀝니다. 진행할까요?</p>
          <div v-for="group in mealMirrorPreview" :key="group.scode" class="meal-mirror__group">
            <div v-if="!group.lines.length" class="meal-mirror__flow">
              <div class="meal-mirror__card meal-mirror__card--in">
                <span class="meal-mirror__tag">세입</span>
                <strong>{{ group.fromName }}</strong>
                <span class="meal-mirror__empty">금액 없음</span>
              </div>
              <span class="meal-mirror__arrow" aria-hidden="true">→</span>
              <div class="meal-mirror__card meal-mirror__card--out">
                <span class="meal-mirror__tag">세출</span>
                <strong>{{ group.toName }}</strong>
                <span class="meal-mirror__empty">반영할 금액 없음</span>
              </div>
            </div>
            <div v-for="(line, lineIndex) in group.lines" :key="lineIndex" class="meal-mirror__flow">
              <div class="meal-mirror__card meal-mirror__card--in">
                <span class="meal-mirror__tag">세입</span>
                <strong>{{ group.fromName }}</strong>
                <span v-if="line.memo" class="meal-mirror__memo">{{ line.memo }}</span>
                <span class="meal-mirror__money">{{ formatMoney(line.amount) }}원</span>
              </div>
              <span class="meal-mirror__arrow" aria-hidden="true">→</span>
              <div class="meal-mirror__card meal-mirror__card--out">
                <span class="meal-mirror__tag">세출</span>
                <strong>{{ group.toName }}</strong>
                <span v-if="line.memo" class="meal-mirror__memo">{{ line.memo }}</span>
                <span class="meal-mirror__money">{{ formatMoney(line.amount) }}원</span>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="meal-mirror-dialog__actions">
          <v-spacer />
          <v-btn variant="text" @click="showMealMirrorDialog = false">닫기</v-btn>
          <v-btn variant="flat" color="primary" :disabled="!mealMirrorCanApply" @click="confirmMealMirror">진행</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSalaryDialog" class="budget-page-dialog" max-width="980" scrollable>
      <v-card class="budget-salary-dialog">
        <v-card-title class="budget-salary-dialog__title">
          임직원 보수 일람표
          <button type="button" class="dialog-close-x" title="닫기" @click="showSalaryDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text>
          <p v-if="!salaryRows.length" class="budget-salary-dialog__empty">표시할 급여 행이 없습니다.</p>
          <table v-else class="budget-salary-table">
            <thead>
              <tr>
                <th>직종</th>
                <th>비용구분</th>
                <th>급여</th>
                <th>퇴직금</th>
                <th>사회보험부담금</th>
                <th>총급여</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in salaryRows" :key="i">
                <td>{{ row.job }}</td>
                <td>{{ row.expense_type }}</td>
                <td class="num">{{ formatMoney(row.pay) }}</td>
                <td class="num">{{ formatMoney(row.out_pay) }}</td>
                <td class="num">{{ formatMoney(row.social_pay) }}</td>
                <td class="num">{{ formatMoney(row.total_pay) }}</td>
              </tr>
            </tbody>
          </table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showSalaryDialog = false">닫기</v-btn>
          <v-btn color="primary" variant="elevated" :disabled="!salaryRows.length" @click="downloadSalaryExcel">엑셀 받기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <BudgetSugaInfoModal
      v-model="showSugaInfoModal"
      :panel-year="searchParams.lebYear"
      @saved="onSugaInfoSaved"
    />

    <BudgetWizard
      v-model="showBudgetWizard"
      :biz-gubun="bizGubun || ''"
      :year="searchParams.lebYear"
      :budget-label="budgetClassLabel"
      :suga-list="effectiveSugaList"
      :tax="effectiveTaxData"
      :has-existing="wizardHasExisting"
      :sheet-rows="wizardSheetRows"
      :draft-key="wizardDraftKey"
      @apply="applyWizardLines"
    />

    <!-- 알림 스낵바 -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top"
    >
      <div class="snackbar-content">
        <v-icon size="20" class="mr-2">{{ snackbar.icon }}</v-icon>
        {{ snackbar.message }}
      </div>
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar.show = false"
        >
          닫기
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, computed, inject } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import { BUDGET_LEAVE_KEY } from "@/composables/budgetLeave";
import { useStore } from "vuex";
import { budgetApi } from "@/services/api";
import { defaultSugaTimesByBizGubun } from "@/utils/budgetBizDefaults";
import { getFundingDisplayName } from "@/utils/fundingCodes";
import BudgetSugaInfoModal from "@/components/budget/BudgetSugaInfoModal.vue";
import BudgetWizard from "@/components/budget/BudgetWizard.vue";
import { budgetWizardFamily, getHiddenScodesWhenEmpty, parseWon, salaryRoleOptions, type WizardLine, type WizardSheetRow } from "@/utils/budgetWizard";
import { BUDGET_GUIDE, BUDGET_POLICY } from "@/constants/budgetPolicy";

const store = useStore();
const isAdmin = computed(() => (store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0) >= 60);
const showMealMirror = computed(() => {
  const family = budgetWizardFamily(props.bizGubun || "");
  return family === "day" || family === "facility";
});

// 수가 인터페이스 (props용)
interface SugaData {
  suga_idx: number;
  suga_biz_gubun: string;
  suga_year: string;
  suga_rank: string;
  suga_money: number;
  suga_bonin: number;
  suga_wdate: string;
}

// 세금/보험 요율 인터페이스
interface TaxData {
  tr_idx?: number;
  tr_year?: string;
  tr_time_pay?: number;   // 최저시급
  tr_yeon?: number;       // 국민연금 요율
  tr_gun?: number;        // 건강보험 요율
  tr_go?: number;         // 고용보험 요율
  tr_jang?: number;       // 장기요양보험 요율
  tr_sil?: number;        // 실업급여 요율
  tr_san?: number;        // 산재보험 요율
  tr_labor_cost_rate?: number; // 인건비 지출 비율
  /** API: 사업구분별 caion_budget_labor_percentage 적용 여부(stepu budget_percentage) */
  labor_rate_source?: "tax_rate" | "biz";
}

// 소득세 테이블 인터페이스
interface SojiData {
  income1: number;
  income2: number;
  depe_1: number;
}

// Props
const props = withDefaults(
  defineProps<{
    memberCode: string;
    bizGubun: string;
    /** true: 시설 상세 예산추경 — 공통 리모컨(.page-sidebar) + 상단 슬롯(탭) + .content-card */
    useMemberPageLayout?: boolean;
    /** true: 리모컨의 전체 삭제 버튼 숨김(추경 페이지는 추경관리 모달에서만 삭제) */
    hideRemoteBudgetDelete?: boolean;
    year?: string;
    budgetClass?: string; // 추경차수: '' = 본예산, '1' = 1차추경, '2' = 2차추경
    sugaList?: SugaData[];
    taxData?: TaxData;
    sojiData?: SojiData[];
  }>(),
  { useMemberPageLayout: false, hideRemoteBudgetDelete: false }
);

interface FinanceCode {
  fc_idx: number;
  fc_trcode: string;
  fc_trname: string;
  fc_year: string;
  fc_lcode: string;
  fc_lname: string;
  fc_mcode: string;
  fc_mname: string;
  fc_scode: string;
  fc_sname: string;
  fc_funding: string;
  fc_hope_scode?: string;
  fc_funding_name?: string;
  budget_amount?: string;
  description?: string;
  unit_price?: string;
  count?: string;
  months?: string;
  hb_idx?: number; // 예산 데이터 고유 ID
  hb_pay_rel?: string; // 급여-보험-퇴직금 연결 키 (고유 문자열)
  hb_pay_rel_manual?: 'Y' | 'N'; // 수동 편집 모드 여부 (Y: 자동계산 안됨)
  lcode_budget_sum?: number; // 관별 예산액 합계
  mcode_budget_sum?: number; // 항별 예산액 합계
  scode_budget_sum?: number; // 목별 예산액 합계
  isSalaryItem?: boolean; // 급여 항목 여부
  isLinkedItem?: boolean; // 급여에 연결된 사회보험/퇴직금 여부
  isRevenuePairItem?: boolean; // 본인부담금-장기요양급여 쌍 항목 여부
  hb_revenue_rel?: string; // 본인부담금-장기요양급여 연결 키 (고유 문자열)
  /** 식재료비(113)↔생계비(311), 시군구(413)↔생계비보조금(312) 연동키 */
  autoLinkId?: string;
  suga_rank?: string; // 선택된 수가 등급 (예: '180분')
  suga_times?: string; // 수가 횟수 (기본값: 20)
}

interface ProcessedFinanceCode extends FinanceCode {
  showTrcode: boolean;
  showLcode: boolean;
  showMcode: boolean;
  showScode: boolean;
  trcodeRowspan: number;
  lcodeRowspan: number;
  mcodeRowspan: number;
  scodeRowspan: number;
  isAddedRow?: boolean; // 추가된 행인지 여부
  hb_pay_rel?: string; // 급여-보험-퇴직금 연결 키 (고유 문자열)
  hb_pay_rel_manual?: 'Y' | 'N'; // 수동 편집 모드 여부 (Y: 자동계산 안됨)
  isLinkedItem?: boolean; // 급여에 연결된 사회보험/퇴직금 여부
  isSalaryItem?: boolean; // 급여 항목 여부
  isRevenuePairItem?: boolean; // 본인부담금-장기요양급여 쌍 항목 여부
  hb_revenue_rel?: string; // 본인부담금-장기요양급여 연결 키 (고유 문자열)
  suga_rank?: string; // 선택된 수가 등급 (예: '180분')
  suga_times?: string; // 수가 횟수 (기본값: 20)
  // 수동편집 모드에서 입력한 값 보존용
  manual_saved_unit_price?: string;
  manual_saved_count?: string;
  manual_saved_months?: string;
}

interface BudgetSheetCell {
  text: string;
  span: number;
}

interface HopeSheetLine {
  key: string;
  bizCode: string;
  bizName: string;
  accountCode: string;
  accountName: string;
  fundCode: string;
  fundName: string;
  amount: number;
  national: string;
  sido: string;
  sigungu: string;
  basis: string;
}

interface BudgetSheetLine {
  key: string;
  spacer: boolean;
  side: "in" | "out";
  totalRow: boolean;
  gubun: BudgetSheetCell;
  lname: BudgetSheetCell;
  mname: BudgetSheetCell;
  sname: string;
  subsidy: number;
  selfPay: number;
  donate: number;
  profit: number;
  total: number;
  item: string;
  basis: string;
}

const searchParams = ref({
  lebYear: props.year || new Date().getFullYear().toString(),
});

const memberCodeStr = computed(() => String(props.memberCode || "").trim());
const hasMemberCode = computed(() => memberCodeStr.value.length > 0);

/** stepu 예산: 사업구분별 112/611 월 횟수(일) 기본 */
const revenuePairDefaultTimes = computed(() =>
  defaultSugaTimesByBizGubun(props.bizGubun || "")
);

const selectedTrcode = ref<string>("ALL");
const saving = ref(false);
const loading = ref(false);
const initialLoading = ref(true); // 초기 페이지 로딩 상태
const showCodeColumns = ref(false); // 관·항 열 표시 — 기본은 목 위주로 접어 둠
const deleting = ref(false);
const showDeleteDialog = ref(false);
const deleteDialogChecking = ref(false);
/** 현재 탭보다 큰 차수가 DB에 남아 있으면 삭제 버튼 비활성(서버 409와 동일 정책) */
const deleteDialogBlocked = ref<string | null>(null);
const showSaveConfirmDialog = ref(false); // 저장 확인 다이얼로그
const saveConfirmNote = ref("");
const showCopyConfirmDialog = ref(false);
const showSavedExportDialog = ref(false);
const savedExportKind = ref<"excel" | "hope" | "salary">("excel");
const showBudgetSheet = ref(false);
const budgetSheetLines = ref<BudgetSheetLine[]>([]);
const showHopeSheet = ref(false);
const hopeSheetLines = ref<HopeSheetLine[]>([]);
const financeCodes = ref<FinanceCode[]>([]);
/** 예산 줄이 붙기 전 재무코드. 마법사 반영 때 목 정보를 여기서 가져온다. */
const financeCatalog = ref<FinanceCode[]>([]);
/** 세입·세출 전체 행(리모컨 합계·저장 기준). 탭 필터와 무관하게 유지 */
const allProcessedData = ref<ProcessedFinanceCode[]>([]);
/** 현재 세입/세출/전체 탭에 보이는 행(allProcessedData와 동일 객체 참조) */
const processedData = ref<ProcessedFinanceCode[]>([]);
const pendingDeleteIds = ref<number[]>([]); // 저장 시 삭제할 hb_idx 목록
/** 서버에서 불러온 직후의 편집 서명. 화면 수정과 다르면 저장 전이다. */
const budgetReady = ref(false);
const savedSignature = ref("");

function budgetEditSignature(): string {
  const rows = allProcessedData.value.map((row) =>
    [
      row.fc_trcode,
      row.fc_scode,
      row.fc_idx,
      row.hb_idx ?? "",
      row.description ?? "",
      row.unit_price ?? "",
      row.count ?? "",
      row.months ?? "",
      row.suga_rank ?? "",
      row.suga_times ?? "",
      row.hb_pay_rel ?? "",
      row.hb_pay_rel_manual ?? "",
      row.hb_revenue_rel ?? "",
      row.autoLinkId ?? "",
      row.isAddedRow ? "1" : "0",
    ].join("\u001f")
  );
  return `${rows.join("\u001e")}|${pendingDeleteIds.value.join(",")}`;
}

function markBudgetClean() {
  savedSignature.value = budgetEditSignature();
  budgetReady.value = true;
}

const budgetDirty = computed(
  () => budgetReady.value && budgetEditSignature() !== savedSignature.value
);
/** 마법사 반영 중 필터를 전체로 바꿀 때 조회가 두 번 돌지 않게 */
const suppressBudgetFetch = ref(false);

/**
 * 탭 필터 상태에서 행 추가/삭제 후 전체 목록(allProcessedData)에 반영.
 * trcodeOfDisplay: 현재 processedData가 어떤 탭 기준인지.
 * 탭 전환 watcher에서는 반드시 "이전 탭"을 넘겨야 한다(이미 selectedTrcode가 바뀐 뒤라서).
 */
const commitProcessedToAll = (trcodeOfDisplay: string = selectedTrcode.value) => {
  if (trcodeOfDisplay === "ALL") {
    allProcessedData.value = processedData.value;
    return;
  }
  const other = allProcessedData.value.filter(
    (row) => row.fc_trcode !== trcodeOfDisplay
  );
  const current = processedData.value;
  if (trcodeOfDisplay === "IN") {
    allProcessedData.value = [...current, ...other.filter((r) => r.fc_trcode === "OUT")];
  } else {
    allProcessedData.value = [...other.filter((r) => r.fc_trcode === "IN"), ...current];
  }
};
const focusedPayRelGroup = ref<string | null>(null); // 포커스된 급여-보험-퇴직금 그룹 키
const showPolicyDialog = ref(false);
const budgetPolicy = BUDGET_POLICY;
const fixedBudgetPolicy = computed(() => budgetPolicy.filter((item) => item.panel !== "suga"));
const sugaPolicyItem = computed(() => budgetPolicy.find((item) => item.panel === "suga") || null);
const budgetGuide = computed(() =>
  BUDGET_GUIDE.filter((item) => {
    if (item.show === "meal") return showMealMirror.value;
    if (item.show === "keep-delete") return !props.hideRemoteBudgetDelete;
    if (item.show === "hide-delete") return props.hideRemoteBudgetDelete;
    return true;
  })
);
const showBudgetWizard = ref(false);
const showSugaInfoModal = ref(false);
const showSalaryDialog = ref(false);
const showMealMirrorDialog = ref(false);
const loadingRecent = ref(false);
const salaryRows = ref<Array<{
  name: string;
  job: string;
  expense_type: string;
  pay: number;
  out_pay: number;
  social_pay: number;
  total_pay: number;
  is_ceo: boolean;
}>>([]);

const loadRecentLabel = computed(() => {
  const bud = props.budgetClass != null ? String(props.budgetClass) : "";
  return !bud || bud === "0" ? "전년도예산복사" : "최근예산복사";
});
const copyConfirmIsMain = computed(() => !props.budgetClass || props.budgetClass === "0");
const savedExportTitle = computed(() => {
  if (savedExportKind.value === "hope") return "희망이음 양식";
  if (savedExportKind.value === "salary") return "보수일람";
  return "예산서 받기";
});
const fetchedSugaList = ref<SugaData[]>([]);
const fetchedTaxData = ref<TaxData | null>(null);

/** props 미전달 시 API(caion_budget_suga)에서 로드 */
const effectiveSugaList = computed(() => {
  if (props.sugaList && props.sugaList.length > 0) return props.sugaList;
  return fetchedSugaList.value;
});

/** props 미전달 시 API(caion_tax_rate)에서 로드 */
const effectiveTaxData = computed((): TaxData | null => {
  if (props.taxData) return props.taxData;
  return fetchedTaxData.value;
});

const taxRatesSourceLabel = computed(() => {
  if (props.taxData) return "이 화면에 적용된 설정";
  if (fetchedTaxData.value) return "연도별 기준 요율";
  return "기본값";
});

type TaxRateDisplayRow = { label: string; value: string; sub?: string };

const taxRatesTableRows = computed((): TaxRateDisplayRow[] => {
  const t = effectiveTaxData.value;
  if (!t) return [];
  const pctOfWage = (v: number | undefined) => {
    if (v === undefined || v === null || !Number.isFinite(Number(v))) return "—";
    return `${(Number(v) * 100).toFixed(2)}%`;
  };
  const pctLabor = () => {
    const v = t.tr_labor_cost_rate;
    if (v === undefined || v === null || !Number.isFinite(Number(v))) return "—";
    return `${Number(v).toFixed(2)}%`;
  };
  const timePay = () => {
    const v = t.tr_time_pay;
    if (v === undefined || v === null) return "— (설정된 값이 없습니다)";
    const n = Number(v);
    if (!Number.isFinite(n)) return "— (값 오류)";
    if (n <= 0) return "0원";
    return `${formatMoney(n)}원`;
  };
  const yr = t.tr_year ? String(t.tr_year) : searchParams.value.lebYear;
  return [
    { label: "적용 연도", value: `${yr}년` },
    { label: "최저시급 (시간급)", value: timePay() },
    { label: "국민연금", value: pctOfWage(t.tr_yeon), sub: "월 급여 총액 대비" },
    { label: "건강보험", value: pctOfWage(t.tr_gun), sub: "월 급여 총액 대비" },
    { label: "고용보험", value: pctOfWage(t.tr_go), sub: "월 급여 총액 대비" },
    { label: "장기요양보험료", value: pctOfWage(t.tr_jang), sub: "건강보험료 × 요율" },
    { label: "실업급여 등", value: pctOfWage(t.tr_sil), sub: "월 급여 총액 대비" },
    { label: "산재보험", value: pctOfWage(t.tr_san), sub: "월 급여 총액 대비" },
    {
      label: "인건비 기준 비율",
      value: pctLabor(),
      sub:
        t.labor_rate_source === "biz"
          ? `사업구분별 기준 · ${(props.bizGubun || "").trim() || "?"} (%)`
          : "연도별 기준 (%)",
    },
  ];
});

function normalizeTaxDataFromApi(raw: Record<string, unknown>): TaxData {
  const num = (key: string): number | undefined => {
    const v = raw[key];
    if (v === undefined || v === null || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  return {
    tr_year: raw.tr_year != null ? String(raw.tr_year) : undefined,
    tr_time_pay: num("tr_time_pay"),
    tr_yeon: num("tr_yeon"),
    tr_gun: num("tr_gun"),
    tr_go: num("tr_go"),
    tr_jang: num("tr_jang"),
    tr_sil: num("tr_sil"),
    tr_san: num("tr_san"),
    tr_labor_cost_rate: num("tr_labor_cost_rate"),
    labor_rate_source: raw.labor_rate_source === "biz" ? "biz" : "tax_rate",
  };
}

async function loadBudgetReferenceData() {
  try {
    const y = searchParams.value.lebYear;
    const biz = props.bizGubun || "";
    const [sRes, tRes] = await Promise.all([
      budgetApi.getSuga({ year: y, bizGubun: biz }),
      budgetApi.getTaxRates({ year: y, bizGubun: biz || undefined }),
    ]);
    const sData = sRes.data as { success?: boolean; data?: SugaData[] };
    if (sData?.success && Array.isArray(sData.data)) {
      fetchedSugaList.value = sData.data;
    }
    const tData = tRes.data as { success?: boolean; data?: Record<string, unknown> };
    if (tData?.success && tData.data && typeof tData.data === "object") {
      fetchedTaxData.value = normalizeTaxDataFromApi(tData.data);
    }
  } catch (e) {
    console.error("예산 참조 데이터(수가/세율) 로드 실패:", e);
  }
}

function openSugaPolicyEdit() {
  if (!isAdmin.value) return;
  showSugaInfoModal.value = true;
}

async function onSugaInfoSaved() {
  await loadBudgetReferenceData();
}

// 알림 스낵바 상태
const snackbar = ref({
  show: false,
  message: '',
  color: 'success',
  icon: 'mdi-check-circle',
  timeout: 3000
});

// 스낵바 표시 헬퍼 함수
const showSnackbar = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
  const config = {
    success: { color: 'success', icon: 'mdi-check-circle' },
    error: { color: 'error', icon: 'mdi-alert-circle' },
    warning: { color: 'warning', icon: 'mdi-alert' },
    info: { color: 'info', icon: 'mdi-information' }
  };
  
  snackbar.value = {
    show: true,
    message,
    color: config[type].color,
    icon: config[type].icon,
    timeout: 3000
  };
};
// focusedRevenuePairGroup은 focusedRevenuePairItem으로 대체됨 (순서 기반 연동)
const sidebarPosition = ref<'left' | 'right'>('left'); // 사이드바 위치 (기본: 좌측)

// 리모컨 접힘(슬림 레일) — 새로고침 후에도 유지
const BUDGET_SIDEBAR_COLLAPSED_KEY = 'caion_budget_remote_collapsed';
const sidebarCollapsed = ref(localStorage.getItem(BUDGET_SIDEBAR_COLLAPSED_KEY) === '1');
const toggleSidebarCollapsed = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  localStorage.setItem(BUDGET_SIDEBAR_COLLAPSED_KEY, sidebarCollapsed.value ? '1' : '0');
};

// 급여-보험-퇴직금 연결 키 생성 (고유 키)
const generatePayRelKey = (): string => {
  return `pay_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// 사이드바 위치 토글
const toggleSidebarPosition = () => {
  sidebarPosition.value = sidebarPosition.value === 'left' ? 'right' : 'left';
};

// 급여 관련 scode (1111: 급여(직접비), 1112: 급여(간접비))
const SALARY_SCODES = ['1111', '1112'];
// 사회보험 관련 scode (1151: 사회보험(직접비), 1152: 사회보험(간접비))
const INSURANCE_SCODES = ['1151', '1152'];
// 퇴직금 관련 scode (1141: 퇴직금(직접비), 1142: 퇴직금(간접비))
const RETIREMENT_SCODES = ['1141', '1142'];
const PAY_LINKED_SCODES = [...INSURANCE_SCODES, ...RETIREMENT_SCODES];
// 본인부담금-장기요양급여 쌍 scode (112: 본인부담금수입, 611: 장기요양급여수입)
const REVENUE_PAIR_SCODES = ['112', '611'];

// 표에서 숨길 scode 목록
/** 사업구분별 — 해당 사업 질의에 없는 목은 비어 있을 때만 숨김 */
const hiddenScodesWhenEmpty = computed(() =>
  getHiddenScodesWhenEmpty(props.bizGubun || "")
);

function salaryOptionRows(kind: "direct" | "indirect") {
  return salaryRoleOptions(kind, props.bizGubun || "").flatMap((role) => {
    const rows = [{ value: `${role} 급여`, label: `${role} 급여`, isCeo: false }];
    if (role === "센터장" || role === "요양보호사" || role === "사회복지사") {
      rows.push({ value: `${role}(대표자) 급여`, label: `${role}(대표자) 급여`, isCeo: true });
    }
    return rows;
  });
}

const getSalaryOptions = (scode: string) => {
  if (scode === "1111") return salaryOptionRows("direct");
  if (scode === "1112") return salaryOptionRows("indirect");
  return [];
};

// 급여/퇴직금/사회보험 정렬 우선순위 (센터장 > 사회복지사 > 요양보호사)
const SALARY_SORT_ORDER: { [key: string]: number } = {
  '센터장': 1,
  '센터장(대표자)': 2,
  '사회복지사': 3,
  '사회복지사(대표자)': 4,
  '요양보호사': 5,
  '요양보호사(대표자)': 6,
};

// 급여/퇴직금/사회보험 항목의 정렬 순서 가져오기
const getSalaryTypeOrder = (description: string): number => {
  if (!description) return 999;
  
  // 급여 항목
  for (const key of Object.keys(SALARY_SORT_ORDER)) {
    if (description.startsWith(key + ' ')) {
      return SALARY_SORT_ORDER[key];
    }
  }
  return 999;
};

// Emits
const emit = defineEmits<{
  (e: 'year-change', year: string): void;
  (e: 'budget-saved'): void;
  (e: 'budget-deleted'): void;
}>();

// 저장 가능한 유효한 항목인지 확인 (내용, 단가, 명회, 개월이 모두 있어야 함)
const isValidForSave = (item: ProcessedFinanceCode): boolean => {
  const description = item.description?.trim() || '';
  const unitPrice = item.unit_price?.replace(/,/g, '') || '0';
  const count = item.count?.replace(/,/g, '') || '0';
  const months = item.months?.replace(/,/g, '') || '0';
  
  // 내용이 없거나, 단가/명회/개월 중 하나라도 0이면 저장 대상 아님
  if (!description) return false;
  if (Number(unitPrice) === 0 && Number(count) === 0 && Number(months) === 0) return false;
  
  return true;
};

const rowHasSaveableContent = (item: ProcessedFinanceCode): boolean =>
  Boolean(item.hb_idx) || isValidForSave(item);

/** 112/611 세입 짝(동일 hb_revenue_rel) 건수 불일치 시 저장 차단 */
function validateRevenuePairsForSave(): string | null {
  const byRel = new Map<string, { n112: number; n611: number }>();
  for (const item of allProcessedData.value) {
    if (item.fc_trcode !== "IN") continue;
    const rel = item.hb_revenue_rel?.trim();
    if (!rel) continue;
    if (!REVENUE_PAIR_SCODES.includes(item.fc_scode)) continue;
    if (!rowHasSaveableContent(item)) continue;
    if (!byRel.has(rel)) byRel.set(rel, { n112: 0, n611: 0 });
    const o = byRel.get(rel)!;
    if (item.fc_scode === "112") o.n112 += 1;
    else o.n611 += 1;
  }
  for (const [, o] of byRel) {
    if (o.n112 !== o.n611) {
      return `본인부담금(112)과 장기요양급여(611)는 같은 연결(짝)마다 개수가 같아야 합니다. (112 ${o.n112}건 · 611 ${o.n611}건) 행을 맞춘 뒤 저장하세요.`;
    }
  }
  return null;
}

/** 급여 그룹(hb_pay_rel)에 보험·퇴직 행이 전혀 없으면 저장 차단 */
function validatePayGroupsForSave(): string | null {
  const byRel = new Map<string, { sal: number; linked: number }>();
  for (const item of allProcessedData.value) {
    if (item.fc_trcode !== "OUT") continue;
    const rel = item.hb_pay_rel?.trim();
    if (!rel) continue;
    if (!rowHasSaveableContent(item)) continue;
    if (!byRel.has(rel)) byRel.set(rel, { sal: 0, linked: 0 });
    const o = byRel.get(rel)!;
    if (SALARY_SCODES.includes(item.fc_scode)) o.sal += 1;
    if (PAY_LINKED_SCODES.includes(item.fc_scode)) o.linked += 1;
  }
  for (const [, o] of byRel) {
    if (o.sal > 0 && o.linked === 0) {
      return "급여(1111/1112)만 입력된 그룹이 있습니다. 같은 그룹에 사회보험·퇴직(1141·1151 등) 행을 두거나, 급여 내용을 비운 뒤 저장하세요.";
    }
  }
  return null;
}

function validateBudgetConsistencyForSave(): string | null {
  return validateRevenuePairsForSave() || validatePayGroupsForSave();
}

const saveAllData = async (): Promise<boolean> => {
  if (!hasMemberCode.value) {
    showSnackbar("기관 정보(memberCode)가 없습니다.", "error");
    return false;
  }

  // 탭에 가려진 세입/세출 행까지 합친 뒤 검증·저장
  commitProcessedToAll();

  const consistencyMsg = validateBudgetConsistencyForSave();
  if (consistencyMsg) {
    showSnackbar(consistencyMsg, "error");
    return false;
  }

  saving.value = true;
  try {
    // 1. 삭제 대기 중인 항목들 먼저 삭제
    if (pendingDeleteIds.value.length > 0) {
      await budgetApi.deleteMultiple(memberCodeStr.value, pendingDeleteIds.value);
      pendingDeleteIds.value = [];
    }

    const validItems = allProcessedData.value.filter((item) => {
      // 기존 데이터(hb_idx가 있는 경우)는 항상 저장 (업데이트)
      if (item.hb_idx) return true;
      // 새 데이터는 유효성 검사 통과해야 저장
      return isValidForSave(item);
    });
    
    // 모든 항목 저장 (hb_idx 유무에 따라 INSERT/UPDATE 구분)
    const budgetList = validItems.map((item) => {
      // 콤마 제거하여 숫자로 변환
      const unitPrice = item.unit_price?.replace(/,/g, "") || "0";
      const count = item.count?.replace(/,/g, "") || "0";
      const months = item.months?.replace(/,/g, "") || "0";
      
      // 정확한 금액 계산 (연결된 항목은 보정된 금액 사용)
      const amount = getExactAmount(item).toString();

      // 대표자 여부 확인 (급여 항목에서 description에 "(대표자)" 포함 여부)
      const isCeo = item.isSalaryItem && item.description?.includes('(대표자)') ? 'Y' : 'N';

      return {
        hb_idx: item.hb_idx || null, // 없으면 null (INSERT)
        trcode: item.fc_trcode, // INSERT 시 필요
        scode: item.fc_scode, // INSERT 시 필요
        hb_pay_rel: item.hb_pay_rel || null,
        hb_pay_rel_manual: item.hb_pay_rel_manual || 'N',
        hb_is_ceo: isCeo,
        hb_revenue_rel: item.hb_revenue_rel || null,
        auto_link: item.autoLinkId || null,
        description: item.description || "",
        unit_price: unitPrice,
        count: count,
        months: months,
        amount: amount,
        fc_funding: item.fc_funding || "",
      };
    });

    const response = await budgetApi.save({
      memberCode: memberCodeStr.value,
      year: searchParams.value.lebYear,
      budClass: props.budgetClass || "",
      bizGubun: props.bizGubun || "",
      budgetList,
    });

    if (response.data.success) {
      await fetchFinanceCodes(true);
      showSnackbar("예산이 성공적으로 저장되었습니다.", "success");
      if (props.useMemberPageLayout) {
        emit("budget-saved");
      }
      return true;
    }
    const msg = (response.data as { message?: string }).message || "알 수 없는 오류";
    showSnackbar(`저장에 실패했습니다: ${msg}`, "error");
    return false;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; status?: number } };
    const msg = err.response?.data?.message || (error instanceof Error ? error.message : "알 수 없는 오류");
    const st = err.response?.status;
    showSnackbar(`저장 중 오류: ${msg}${st ? ` (${st})` : ""}`, "error");
    console.error("저장 오류:", error);
    return false;
  } finally {
    saving.value = false;
  }
};

function openSaveDialog(note = "") {
  saveConfirmNote.value = note;
  showSaveConfirmDialog.value = true;
}

// 저장 확인 후 실제 저장 수행
const confirmSave = async () => {
  showSaveConfirmDialog.value = false;
  saveConfirmNote.value = "";
  await saveAllData();
};

const showLeaveDialog = ref(false);
const leavingSave = ref(false);
let leaveResolve: ((ok: boolean) => void) | null = null;
let skipBeforeUnload = false;

const budgetLeaveSlot = inject(BUDGET_LEAVE_KEY, null);

function finishLeave(ok: boolean) {
  showLeaveDialog.value = false;
  const resolve = leaveResolve;
  leaveResolve = null;
  resolve?.(ok);
}

function cancelLeave() {
  if (leavingSave.value) return;
  finishLeave(false);
}

function leaveWithoutSave() {
  if (leavingSave.value) return;
  finishLeave(true);
}

async function leaveAfterSave() {
  if (leavingSave.value) return;
  leavingSave.value = true;
  try {
    const ok = await saveAllData();
    finishLeave(ok);
  } finally {
    leavingSave.value = false;
  }
}

/** 저장하지 않은 수정이 있으면 머물지, 버릴지, 저장하고 나갈지 묻는다. */
function confirmDiscard(): Promise<boolean> {
  commitProcessedToAll();
  if (!budgetDirty.value) return Promise.resolve(true);
  if (leaveResolve) return Promise.resolve(false);
  showLeaveDialog.value = true;
  return new Promise((resolve) => {
    leaveResolve = resolve;
  });
}

function allowNextUnload() {
  skipBeforeUnload = true;
  window.setTimeout(() => {
    skipBeforeUnload = false;
  }, 1500);
}

function onBudgetBeforeUnload(event: BeforeUnloadEvent) {
  if (skipBeforeUnload) return;
  commitProcessedToAll();
  if (!budgetDirty.value) return;
  event.preventDefault();
  event.returnValue = "";
}

onBeforeRouteLeave(() => confirmDiscard());

defineExpose({ confirmDiscard });

// 예산 데이터 백엔드에서 불러오기
// baseCodes: 방금 받은 재무코드 원본. 생략 시 financeCodes를 쓰지만,
// 동시 조회가 엇갈릴 때 확장된 행 위에 다시 매핑되어 행이 배가되는 것을 막기 위해
// fetchFinanceCodes에서는 항상 신선한 원본을 넘긴다.
const loadBudgetData = async (baseCodes?: FinanceCode[]) => {
  if (!hasMemberCode.value) {
    return;
  }

  try {
    // 리모컨 세입·세출 합계가 탭과 무관히 유지되도록 항상 전체 라인을 조회
    const response = await budgetApi.getLines({
      memberCode: memberCodeStr.value,
      year: searchParams.value.lebYear,
      gubun: undefined,
      budClass: props.budgetClass || "",
      bizGubun: props.bizGubun || "",
    });

    if (response.data.success && response.data.data) {
      const budgetData = response.data.data;

      // 예산 데이터를 financeCodes에 매핑
      // 같은 scode에 여러 행이 있을 수 있으므로 먼저 그룹화
      const budgetByScodeMap = new Map<string, any[]>();
      budgetData.forEach((bd: any) => {
        const key = `${bd.hb_gubun}-${bd.hb_scode}`;
        if (!budgetByScodeMap.has(key)) {
          budgetByScodeMap.set(key, []);
        }
        budgetByScodeMap.get(key)?.push(bd);
      });

      // 새로운 financeCodes 배열 생성 (여러 행 지원)
      const newFinanceCodes: FinanceCode[] = [];

      (baseCodes ?? financeCodes.value).forEach((fc) => {
        const key = `${fc.fc_trcode}-${fc.fc_scode}`;
        const matchingBudgets = budgetByScodeMap.get(key);

        if (matchingBudgets && matchingBudgets.length > 0) {
          // 각 예산 데이터에 대해 행 생성
          matchingBudgets.forEach((budget) => {
            const isSalary = SALARY_SCODES.includes(fc.fc_scode);
            const isInsurance = INSURANCE_SCODES.includes(fc.fc_scode);
            const isRetirement = RETIREMENT_SCODES.includes(fc.fc_scode);
            const isLinked = (isInsurance || isRetirement) && budget.hb_pay_rel;
            // 수입 쌍(112/611)은 세입(IN)에서만 유효
            const isRevenuePair = fc.fc_trcode === 'IN' && REVENUE_PAIR_SCODES.includes(fc.fc_scode);
            const lineFunding =
              budget.hb_fc_funding != null && String(budget.hb_fc_funding).trim() !== ""
                ? String(budget.hb_fc_funding).trim()
                : null;
            const mergedFunding = lineFunding ?? (fc.fc_funding || "");

            // 본인부담금/장기요양급여의 경우 description에서 수가 정보 파싱
            let sugaRank = '';
            let sugaTimes = '';
            if (isRevenuePair && budget.hb_memo) {
              // "180분 X 20" 또는 "180분수가 X 20" 형태 파싱
              const match = budget.hb_memo.match(/^(.+?)\s*[Xx×]\s*(\d+)(?:\s*기초)?$/);
              if (match) {
                sugaRank = match[1].replace('수가', '').trim();
                sugaTimes = match[2];
              }
            }
            
            newFinanceCodes.push({
              ...fc,
              fc_funding: mergedFunding,
              fc_funding_name: getFundingDisplayName(mergedFunding) || fc.fc_funding_name,
              hb_idx: budget.hb_idx,
              hb_pay_rel: budget.hb_pay_rel || null,
              hb_pay_rel_manual: budget.hb_pay_rel_manual || 'N',
              hb_revenue_rel: budget.hb_revenue_rel || null,
              autoLinkId: budget.hb_auto_link || undefined,
              description: budget.hb_memo || "",
              unit_price: budget.hb_price ? Number(budget.hb_price).toLocaleString() : "",
              count: budget.hb_person ? Number(budget.hb_person).toLocaleString() : "",
              months: budget.hb_month ? Number(budget.hb_month).toLocaleString() : "",
              isSalaryItem: isSalary,
              isLinkedItem: isLinked,
              isRevenuePairItem: isRevenuePair,  // fc_scode 기반 (112/611)
              suga_rank: isRevenuePair ? sugaRank : undefined,
              suga_times: isRevenuePair ? sugaTimes : undefined,
            });
          });
        } else {
          // 예산 데이터가 없으면 기본 행만 추가
          // 수입 쌍(112/611)은 세입(IN)에서만 유효
          const isRevenuePair = fc.fc_trcode === 'IN' && REVENUE_PAIR_SCODES.includes(fc.fc_scode);
          newFinanceCodes.push({
            ...fc,
            isRevenuePairItem: isRevenuePair,
            suga_rank: isRevenuePair ? '' : undefined,
            suga_times: isRevenuePair ? '' : undefined,
            months: isRevenuePair ? '' : fc.months,
          });
        }
      });

      financeCodes.value = newFinanceCodes;

      // 저장 후 로드 시 이전 편집 상태가 남지 않도록 초기화
      allProcessedData.value = [];
      processedData.value = [];
      processFinanceCodes();
      markBudgetClean();
    } else {
      processFinanceCodes();
    }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; status?: number } };
    const msg = err.response?.data?.message || (error instanceof Error ? error.message : "알 수 없는 오류");
    showSnackbar(`예산 조회 실패: ${msg}`, "error");
    console.error("예산 데이터 조회 오류:", error);
    processFinanceCodes();
  }
};

// 재무코드 조회 (silent: true면 로딩 표시 없이 데이터만 갱신)
// seq: 탭·연도를 빠르게 바꿀 때 늦게 도착한 옛 응답이 새 상태를 덮지 않도록 한다
let budgetFetchSeq = 0;

const fetchFinanceCodes = async (silent: boolean = false) => {
  if (!hasMemberCode.value) {
    if (!silent) {
      loading.value = false;
      initialLoading.value = false;
    }
    return;
  }

  const seq = ++budgetFetchSeq;
  budgetReady.value = false;

  if (!silent) {
    loading.value = true;
    allProcessedData.value = [];
    processedData.value = [];
  }

  try {
    await loadBudgetReferenceData();
    // 세입/세출 탭은 표시 필터만 담당. 합계·저장을 위해 항상 양쪽을 불러온다.
    const [inResponse, outResponse] = await Promise.all([
      budgetApi.getFinanceCodes({
        year: searchParams.value.lebYear,
        trcode: "IN",
      }),
      budgetApi.getFinanceCodes({
        year: searchParams.value.lebYear,
        trcode: "OUT",
      }),
    ]);

    if (seq !== budgetFetchSeq) return;
    const inData = inResponse.data.success ? inResponse.data.data : [];
    const outData = outResponse.data.success ? outResponse.data.data : [];
    const freshCodes = [...inData, ...outData];
    financeCodes.value = freshCodes;
    financeCatalog.value = freshCodes.map((fc) => ({ ...fc }));
    await loadBudgetData(freshCodes);
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; status?: number } };
    const msg = err.response?.data?.message || (error instanceof Error ? error.message : "알 수 없는 오류");
    showSnackbar(`재무코드 조회 실패: ${msg}`, "error");
    console.error("재무코드 조회 오류:", error);
    financeCodes.value = [];
    allProcessedData.value = [];
    processedData.value = [];
    markBudgetClean();
  } finally {
    if (!silent && seq === budgetFetchSeq) {
      loading.value = false;
    }
    // 초기 로딩 완료 (약간의 딜레이로 부드러운 전환)
    if (initialLoading.value) {
      setTimeout(() => {
        initialLoading.value = false;
      }, 100);
    }
  }
};

// rowspan 계산을 위한 데이터 가공
const processFinanceCodes = () => {
  const data: ProcessedFinanceCode[] = [];
  
  // financeCodes를 기준으로 처리하되, 기존 전체 행에서 편집된 데이터와 추가된 행을 병합
  const existingDataMap = new Map<number, ProcessedFinanceCode>();
  const addedRows: ProcessedFinanceCode[] = [];
  
  // 탭에 가려진 행까지 포함해 병합
  allProcessedData.value.forEach(item => {
    if (item.isAddedRow) {
      addedRows.push(item);
    } else if (item.fc_idx) {
      existingDataMap.set(item.fc_idx, item);
    }
  });
  
  // financeCodes를 기준으로 sourceData 생성
  const sourceData: any[] = financeCodes.value.map(item => {
    const existing = existingDataMap.get(item.fc_idx);
    if (existing) {
      // 기존 편집 데이터가 있으면 그것을 사용
      return {
        ...item,
        hb_idx: existing.hb_idx || item.hb_idx,
        budget_amount: existing.budget_amount !== undefined ? existing.budget_amount : (item.budget_amount || ""),
        description: existing.description !== undefined ? existing.description : (item.description || ""),
        unit_price: existing.unit_price !== undefined ? existing.unit_price : (item.unit_price || ""),
        count: existing.count !== undefined ? existing.count : (item.count || ""),
        months: existing.months !== undefined ? existing.months : (item.months || ""),
        lcode_budget_sum: item.lcode_budget_sum || 0,
        mcode_budget_sum: item.mcode_budget_sum || 0,
        scode_budget_sum: item.scode_budget_sum || 0,
        isAddedRow: false
      };
    } else {
      return {
        ...item,
        hb_idx: item.hb_idx,
        budget_amount: item.budget_amount || "",
        description: item.description || "",
        unit_price: item.unit_price || "",
        count: item.count || "",
        months: item.months || "",
        lcode_budget_sum: item.lcode_budget_sum || 0,
        mcode_budget_sum: item.mcode_budget_sum || 0,
        scode_budget_sum: item.scode_budget_sum || 0,
        isAddedRow: false
      };
    }
  });
  
  // 추가된 행(isAddedRow: true)은 유지
  sourceData.push(...addedRows);
  
  // 사업구분 질의에 없는 목은 비어 있을 때만 숨긴다. 금액이 있으면 표에 남긴다.
  const hideIn = hiddenScodesWhenEmpty.value.in;
  const hideOut = hiddenScodesWhenEmpty.value.out;
  const filteredSourceData = sourceData.filter(item => {
    const hiddenIn = item.fc_trcode === 'IN' && hideIn.includes(item.fc_scode);
    const hiddenOut = item.fc_trcode === 'OUT' && hideOut.includes(item.fc_scode);
    if (!hiddenIn && !hiddenOut) return true;
    const unit = Number(String(item.unit_price || "").replace(/,/g, "")) || 0;
    const count = Number(String(item.count || "").replace(/,/g, "")) || 0;
    const months = Number(String(item.months || "").replace(/,/g, "")) || 0;
    const memo = String(item.description || "").trim();
    return unit * count * months > 0 || Boolean(memo);
  });

  // 급여/퇴직금/사회보험 항목 정렬 (센터장 > 사회복지사 > 요양보호사 순서)
  // 같은 scode 그룹 내에서만 정렬
  const sortedSourceData = [...filteredSourceData].sort((a, b) => {
    // 먼저 trcode, lcode, mcode, scode 순서대로 비교
    if (a.fc_trcode !== b.fc_trcode) return a.fc_trcode.localeCompare(b.fc_trcode);
    if (a.fc_lcode !== b.fc_lcode) return a.fc_lcode.localeCompare(b.fc_lcode);
    if (a.fc_mcode !== b.fc_mcode) return a.fc_mcode.localeCompare(b.fc_mcode);
    if (a.fc_scode !== b.fc_scode) return a.fc_scode.localeCompare(b.fc_scode);
    
    // 같은 scode 내에서 급여/퇴직금/사회보험 관련 항목인지 확인
    const isSalaryRelatedA = SALARY_SCODES.includes(a.fc_scode) || 
                            INSURANCE_SCODES.includes(a.fc_scode) || 
                            RETIREMENT_SCODES.includes(a.fc_scode);
    const isSalaryRelatedB = SALARY_SCODES.includes(b.fc_scode) || 
                            INSURANCE_SCODES.includes(b.fc_scode) || 
                            RETIREMENT_SCODES.includes(b.fc_scode);
    
    // 급여 관련 항목이면 description 기준 정렬
    if (isSalaryRelatedA && isSalaryRelatedB) {
      const orderA = getSalaryTypeOrder(a.description || '');
      const orderB = getSalaryTypeOrder(b.description || '');
      return orderA - orderB;
    }
    
    return 0;
  });

  // rowspan 카운트
  const trcodeGroups: { [key: string]: number } = {};
  const lcodeGroups: { [key: string]: number } = {};
  const mcodeGroups: { [key: string]: number } = {};
  const scodeGroups: { [key: string]: number } = {};

  sortedSourceData.forEach((item) => {
    const trcodeKey = `${item.fc_trcode}`;
    const lcodeKey = `${item.fc_trcode}-${item.fc_lcode}`;
    const mcodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}`;
    const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;

    trcodeGroups[trcodeKey] = (trcodeGroups[trcodeKey] || 0) + 1;
    lcodeGroups[lcodeKey] = (lcodeGroups[lcodeKey] || 0) + 1;
    mcodeGroups[mcodeKey] = (mcodeGroups[mcodeKey] || 0) + 1;
    scodeGroups[scodeKey] = (scodeGroups[scodeKey] || 0) + 1;
  });

  // 2차: 표시 여부 결정
  let prevTrcode = "";
  let prevLcode = "";
  let prevMcode = "";
  let prevScode = "";
  let trcodeCount = 0;
  let lcodeCount = 0;
  let mcodeCount = 0;
  let scodeCount = 0;

  sortedSourceData.forEach((item) => {
    const trcodeKey = `${item.fc_trcode}`;
    const lcodeKey = `${item.fc_trcode}-${item.fc_lcode}`;
    const mcodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}`;
    const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;

    const showTrcode = item.fc_trcode !== prevTrcode;
    const showLcode = lcodeKey !== `${prevTrcode}-${prevLcode}`;
    const showMcode = mcodeKey !== `${prevTrcode}-${prevLcode}-${prevMcode}`;
    const showScode = scodeKey !== `${prevTrcode}-${prevLcode}-${prevMcode}-${prevScode}`;

    if (showTrcode) {
      prevTrcode = item.fc_trcode;
      trcodeCount = 0;
    }

    if (showLcode) {
      prevLcode = item.fc_lcode;
      lcodeCount = 0;
    }

    if (showMcode) {
      prevMcode = item.fc_mcode;
      mcodeCount = 0;
    }

    if (showScode) {
      prevScode = item.fc_scode;
      scodeCount = 0;
    }

    data.push({
      ...item,
      showTrcode: trcodeCount === 0,
      showLcode: lcodeCount === 0,
      showMcode: mcodeCount === 0,
      showScode: scodeCount === 0,
      trcodeRowspan: trcodeGroups[trcodeKey],
      lcodeRowspan: lcodeGroups[lcodeKey],
      mcodeRowspan: mcodeGroups[mcodeKey],
      scodeRowspan: scodeGroups[scodeKey],
    });

    trcodeCount++;
    lcodeCount++;
    mcodeCount++;
    scodeCount++;
  });

  allProcessedData.value = data;
  refreshDisplayByTrcode();
};

const fundingCellLabel = (item: ProcessedFinanceCode) => {
  const c = (item.fc_funding || "").trim();
  if (!c) return "-";
  return getFundingDisplayName(c);
};

// 숫자 포맷팅
const formatNumber = (event: any, item: any, field: string) => {
  const value = event.target.value.replace(/[^0-9]/g, "");
  item[field] = value ? Number(value).toLocaleString() : "";
};

const COPY_SNAME_SKIP = new Set(['112', '611', '1111', '1112', '1141', '1142', '1151', '1152']);

/** 목 이름을 더블클릭하면 내용란에 같은 글자를 넣는다 */
function copySnameToDescription(item: ProcessedFinanceCode) {
  if (COPY_SNAME_SKIP.has(item.fc_scode)) return;
  if (item.isSalaryItem || item.isRevenuePairItem || item.isLinkedItem) return;
  item.description = item.fc_sname || '';
}

const INCOME_EXPENSE_MIRROR: Record<string, { out: string; label: string; fromName: string }> = {
  '113': { out: '311', label: '생계비', fromName: '식재료비' },
  '413': { out: '312', label: '생계비(보조금)', fromName: '시군구보조금' },
};

/** 식재료비·시군구보조금 세입을 세출 생계비·생계비(보조금)에 맞춰 만든다 */
function syncIncomeToExpense(item: ProcessedFinanceCode): boolean {
  if (item.fc_trcode !== 'IN') return false;
  const spec = INCOME_EXPENSE_MIRROR[item.fc_scode];
  if (!spec) return false;
  const unit = Number(String(item.unit_price || '').replace(/,/g, '')) || 0;
  const count = Number(String(item.count || '').replace(/,/g, '')) || 0;
  const months = Number(String(item.months || '').replace(/,/g, '')) || 0;
  const memo = String(item.description || '').trim();
  if (unit * count * months <= 0 && !memo) return false;

  if (!item.autoLinkId) {
    item.autoLinkId = `al_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }
  let out = allProcessedData.value.find(
    (row) => row.fc_trcode === 'OUT' && row.fc_scode === spec.out && row.autoLinkId === item.autoLinkId
  );
  if (!out) {
    const fc = findFinanceCodeByScode('OUT', spec.out);
    if (!fc) return false;
    out = {
      ...fc,
      hb_idx: undefined,
      description: memo || spec.label,
      unit_price: item.unit_price || '',
      count: item.count || '',
      months: item.months || '',
      autoLinkId: item.autoLinkId,
      isAddedRow: true,
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    allProcessedData.value.push(out);
    if (selectedTrcode.value === 'ALL') {
      processedData.value = allProcessedData.value;
    } else if (selectedTrcode.value === 'OUT') {
      processedData.value = allProcessedData.value.filter((row) => row.fc_trcode === 'OUT');
    }
    recalculateRowspans();
  }
  out.unit_price = item.unit_price || '';
  out.count = item.count || '';
  out.months = item.months || '';
  if (memo) out.description = memo;
  return true;
}

function mealLineAmount(row: ProcessedFinanceCode) {
  const unit = Number(String(row.unit_price || '').replace(/,/g, '')) || 0;
  const count = Number(String(row.count || '').replace(/,/g, '')) || 0;
  const months = Number(String(row.months || '').replace(/,/g, '')) || 0;
  return unit * count * months;
}

const mealMirrorPreview = computed(() =>
  Object.entries(INCOME_EXPENSE_MIRROR).map(([scode, spec]) => {
    const lines = allProcessedData.value
      .filter((row) => row.fc_trcode === 'IN' && row.fc_scode === scode)
      .map((row) => ({
        memo: String(row.description || '').trim(),
        amount: mealLineAmount(row),
        ready: mealLineAmount(row) > 0 || Boolean(String(row.description || '').trim()),
      }))
      .filter((line) => line.ready);
    return {
      scode,
      fromName: spec.fromName,
      toName: spec.label,
      lines,
    };
  })
);

const mealMirrorCanApply = computed(() => mealMirrorPreview.value.some((group) => group.lines.length > 0));

function openMealMirrorDialog() {
  showMealMirrorDialog.value = true;
}

/** 모달에서 확인한 뒤에만 식재료비·시군구보조금을 세출에 반영 */
function confirmMealMirror() {
  const ready = allProcessedData.value.filter((row) => {
    if (row.fc_trcode !== 'IN' || !INCOME_EXPENSE_MIRROR[row.fc_scode]) return false;
    return mealLineAmount(row) > 0 || Boolean(String(row.description || '').trim());
  });
  if (!ready.length) return;
  let applied = 0;
  ready.forEach((row) => {
    if (syncIncomeToExpense(row)) applied += 1;
  });
  showMealMirrorDialog.value = false;
  if (applied) {
    openSaveDialog(`${applied}건을 세출에 반영했습니다. 저장해야 서버에 남습니다.`);
    return;
  }
  showSnackbar('반영할 세출 목을 찾지 못했습니다.', 'warning');
}

// 금액 계산 (단가 × 명(회) × 개월)
// 연결된 항목(퇴직금, 사회보험)의 경우 정확한 총액으로 보정
const calculateAmount = (item: ProcessedFinanceCode) => {
  const unitPrice = Number(item.unit_price?.replace(/,/g, "") || 0);
  const count = Number(item.count?.replace(/,/g, "") || 0);
  const months = Number(item.months?.replace(/,/g, "") || 0);
  
  // 연결된 항목(퇴직금, 사회보험)인 경우 정확한 총액 계산
  if (item.isLinkedItem && item.hb_pay_rel && item.hb_pay_rel_manual !== 'Y') {
    // 연결된 급여 항목 찾기 (탭 필터와 무관하게 전체에서)
    const salaryItem = allProcessedData.value.find(
      row => row.hb_pay_rel === item.hb_pay_rel && row.isSalaryItem
    );
    
    if (salaryItem) {
      const salaryUnitPrice = Number(salaryItem.unit_price?.replace(/,/g, "") || 0);
      const salaryCount = Number(salaryItem.count?.replace(/,/g, "") || 0);
      const salaryMonths = Number(salaryItem.months?.replace(/,/g, "") || 0);
      const isCeo = (salaryItem.description || '').includes('(대표자)');
      const centerOwner = isCenterOwnerPay(salaryItem.description);
      
      // 퇴직금인 경우: 정확한 총액 = 월 총 급여 × 개월 / 12
      if (RETIREMENT_SCODES.includes(item.fc_scode)) {
        const exactTotal = isCeo ? 0 : Math.round((salaryUnitPrice * salaryCount * salaryMonths) / 12);
        return exactTotal > 0 ? exactTotal.toLocaleString() : "-";
      }
      
      // 사회보험인 경우: 정확한 총액 = 월 총 급여 × 요율들의 합 × 개월
      if (INSURANCE_SCODES.includes(item.fc_scode) && effectiveTaxData.value) {
        if (centerOwner) return "-";
        const totalMonthlySalary = salaryUnitPrice * salaryCount;
        
        const nationalPension = Math.round(totalMonthlySalary * (effectiveTaxData.value.tr_yeon || 0));
        const healthInsurance = Math.round(totalMonthlySalary * (effectiveTaxData.value.tr_gun || 0));
        const longTermCare = Math.round(healthInsurance * (effectiveTaxData.value.tr_jang || 0));
        const employmentInsurance = isCeo ? 0 : Math.round(totalMonthlySalary * (effectiveTaxData.value.tr_go || 0));
        const unemployment = isCeo ? 0 : Math.round(totalMonthlySalary * (effectiveTaxData.value.tr_sil || 0));
        const industrialAccident = isCeo ? 0 : Math.round(totalMonthlySalary * (effectiveTaxData.value.tr_san || 0));
        
        const monthlyInsurance = nationalPension + healthInsurance + longTermCare + 
                                 employmentInsurance + unemployment + industrialAccident;
        const exactTotal = monthlyInsurance * salaryMonths;
        return exactTotal > 0 ? exactTotal.toLocaleString() : "-";
      }
    }
  }
  
  // 일반 항목: 단순 계산
  const amount = unitPrice * count * months;
  return amount > 0 ? amount.toLocaleString() : "-";
};

// 정확한 금액 계산 (숫자 반환) - 연결된 항목의 경우 보정된 금액 반환
const getExactAmount = (item: ProcessedFinanceCode): number => {
  const unitPrice = Number(item.unit_price?.replace(/,/g, "") || 0);
  const count = Number(item.count?.replace(/,/g, "") || 0);
  const months = Number(item.months?.replace(/,/g, "") || 0);
  
  // 연결된 항목(퇴직금, 사회보험)인 경우 정확한 총액 계산
  if (item.isLinkedItem && item.hb_pay_rel && item.hb_pay_rel_manual !== 'Y') {
    // 연결된 급여 항목 찾기 (탭 필터와 무관하게 전체에서)
    const salaryItem = allProcessedData.value.find(
      row => row.hb_pay_rel === item.hb_pay_rel && row.isSalaryItem
    );
    
    if (salaryItem) {
      const salaryUnitPrice = Number(salaryItem.unit_price?.replace(/,/g, "") || 0);
      const salaryCount = Number(salaryItem.count?.replace(/,/g, "") || 0);
      const salaryMonths = Number(salaryItem.months?.replace(/,/g, "") || 0);
      const isCeo = (salaryItem.description || '').includes('(대표자)');
      const centerOwner = isCenterOwnerPay(salaryItem.description);
      
      // 퇴직금인 경우: 정확한 총액 = 월 총 급여 × 개월 / 12
      if (RETIREMENT_SCODES.includes(item.fc_scode)) {
        return isCeo ? 0 : Math.round((salaryUnitPrice * salaryCount * salaryMonths) / 12);
      }
      
      // 사회보험인 경우: 정확한 총액 = 월 총 급여 × 요율들의 합 × 개월
      if (INSURANCE_SCODES.includes(item.fc_scode) && effectiveTaxData.value) {
        if (centerOwner) return 0;
        const totalMonthlySalary = salaryUnitPrice * salaryCount;
        
        const nationalPension = Math.round(totalMonthlySalary * (effectiveTaxData.value.tr_yeon || 0));
        const healthInsurance = Math.round(totalMonthlySalary * (effectiveTaxData.value.tr_gun || 0));
        const longTermCare = Math.round(healthInsurance * (effectiveTaxData.value.tr_jang || 0));
        const employmentInsurance = isCeo ? 0 : Math.round(totalMonthlySalary * (effectiveTaxData.value.tr_go || 0));
        const unemployment = isCeo ? 0 : Math.round(totalMonthlySalary * (effectiveTaxData.value.tr_sil || 0));
        const industrialAccident = isCeo ? 0 : Math.round(totalMonthlySalary * (effectiveTaxData.value.tr_san || 0));
        
        const monthlyInsurance = nationalPension + healthInsurance + longTermCare + 
                                 employmentInsurance + unemployment + industrialAccident;
        return monthlyInsurance * salaryMonths;
      }
    }
  }
  
  // 일반 항목: 단순 계산
  return unitPrice * count * months;
};

const wizardHasExisting = computed(() =>
  allProcessedData.value.some((row) => Boolean(row.hb_idx) || getExactAmount(row) > 0)
);

const wizardDraftKey = computed(() =>
  [memberCodeStr.value, searchParams.value.lebYear, props.bizGubun || "", props.budgetClass || "0"].join("|")
);

const wizardSheetRows = computed((): WizardSheetRow[] =>
  allProcessedData.value.map((item) => ({
    trcode: item.fc_trcode,
    scode: item.fc_scode,
    description: item.description || "",
    unitPrice: parseWon(item.unit_price),
    count: parseWon(item.count),
    months: parseWon(item.months),
    sugaRank: item.suga_rank || "",
    sugaTimes: item.suga_times || "",
    isSalary: Boolean(item.isSalaryItem),
    isLinked: Boolean(item.isLinkedItem),
  }))
);

function openBudgetWizard() {
  commitProcessedToAll();
  showBudgetWizard.value = true;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function openCopyConfirm() {
  if (!hasMemberCode.value) {
    showSnackbar("기관 정보(memberCode)가 없습니다.", "error");
    return;
  }
  showCopyConfirmDialog.value = true;
}

async function runCopyRecent() {
  if (!hasMemberCode.value) {
    showSnackbar("기관 정보(memberCode)가 없습니다.", "error");
    return;
  }
  showCopyConfirmDialog.value = false;
  loadingRecent.value = true;
  try {
    const res = await budgetApi.loadRecent({
      memberCode: memberCodeStr.value,
      year: searchParams.value.lebYear,
      budClass: props.budgetClass || "",
      bizGubun: props.bizGubun || "",
    });
    if (!res.data?.success) {
      showSnackbar(res.data?.message || "복사에 실패했습니다.", "error");
      return;
    }
    showSnackbar(res.data.message || "복사가 완료되었습니다.", "success");
    await fetchFinanceCodes(true);
    if (props.useMemberPageLayout) emit("budget-saved");
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    showSnackbar(err.response?.data?.message || "복사 중 오류가 발생했습니다.", "error");
  } finally {
    loadingRecent.value = false;
  }
}

function splitSheetFunding(code: string, amount: number) {
  const fund = (code || "").trim();
  return {
    subsidy: fund === "07" ? amount : 0,
    selfPay: fund === "04" ? amount : 0,
    donate: fund === "05" ? amount : 0,
    profit: fund === "06" || !fund ? amount : 0,
  };
}

function markSheetSpans(
  lines: BudgetSheetLine[],
  pick: (row: BudgetSheetLine) => string,
  write: (row: BudgetSheetLine, span: number) => void
) {
  let i = 0;
  while (i < lines.length) {
    const key = pick(lines[i]);
    let j = i + 1;
    while (j < lines.length && pick(lines[j]) === key) j += 1;
    write(lines[i], j - i);
    for (let k = i + 1; k < j; k += 1) write(lines[k], 0);
    i = j;
  }
}

function blankSheetLine(key: string): BudgetSheetLine {
  return {
    key,
    spacer: true,
    side: "in",
    totalRow: false,
    gubun: { text: "", span: 0 },
    lname: { text: "", span: 0 },
    mname: { text: "", span: 0 },
    sname: "",
    subsidy: 0,
    selfPay: 0,
    donate: 0,
    profit: 0,
    total: 0,
    item: "",
    basis: "",
  };
}

function buildBudgetSheetLines(): BudgetSheetLine[] {
  commitProcessedToAll();
  type Acc = {
    side: "in" | "out";
    lname: string;
    mname: string;
    sname: string;
    subsidy: number;
    selfPay: number;
    donate: number;
    profit: number;
    total: number;
    items: string[];
    bases: string[];
  };
  const groups = new Map<string, Acc>();
  allProcessedData.value.forEach((item) => {
    const amount = getExactAmount(item);
    if (!amount) return;
    const side: "in" | "out" = item.fc_trcode === "IN" ? "in" : "out";
    const lname = item.fc_lname || "";
    const mname = item.fc_mname || "";
    const sname = item.fc_sname || "";
    const key = `${side}|${lname}|${mname}|${sname}`;
    const fund = splitSheetFunding(item.fc_funding || "", amount);
    const memo = (item.description || "").trim();
    const unit = Number(String(item.unit_price || "").replace(/,/g, "")) || 0;
    const count = Number(String(item.count || "").replace(/,/g, "")) || 0;
    const months = Number(String(item.months || "").replace(/,/g, "")) || 0;
    const multiplied = unit * count * months;
    const basis = multiplied === amount
      ? `${memo} : ${addComma(unit)} x ${addComma(count)} x ${addComma(months)} = ${addComma(amount)}`
      : `${memo} : ${addComma(amount)}`;
    const prev = groups.get(key);
    if (!prev) {
      groups.set(key, {
        side,
        lname,
        mname,
        sname,
        subsidy: fund.subsidy,
        selfPay: fund.selfPay,
        donate: fund.donate,
        profit: fund.profit,
        total: amount,
        items: memo ? [memo] : [],
        bases: [basis],
      });
      return;
    }
    prev.subsidy += fund.subsidy;
    prev.selfPay += fund.selfPay;
    prev.donate += fund.donate;
    prev.profit += fund.profit;
    prev.total += amount;
    if (memo && !prev.items.includes(memo)) prev.items.push(memo);
    if (!prev.bases.includes(basis)) prev.bases.push(basis);
  });

  const makeSide = (side: "in" | "out"): BudgetSheetLine[] => {
    const label = side === "in" ? "세입" : "세출";
    const rows: BudgetSheetLine[] = [];
    groups.forEach((group) => {
      if (group.side !== side) return;
      rows.push({
        key: `${side}-${rows.length}-${group.lname}-${group.mname}-${group.sname}`,
        spacer: false,
        side,
        totalRow: false,
        gubun: { text: label, span: 1 },
        lname: { text: group.lname, span: 1 },
        mname: { text: group.mname, span: 1 },
        sname: group.sname,
        subsidy: group.subsidy,
        selfPay: group.selfPay,
        donate: group.donate,
        profit: group.profit,
        total: group.total,
        item: group.items.join("\n"),
        basis: group.bases.join("\n"),
      });
    });
    if (!rows.length) return [];
    markSheetSpans(rows, () => side, (row, span) => { row.gubun.span = span; });
    markSheetSpans(rows, (row) => row.lname.text, (row, span) => { row.lname.span = span; });
    markSheetSpans(rows, (row) => `${row.lname.text}|${row.mname.text}`, (row, span) => { row.mname.span = span; });
    const sum = rows.reduce(
      (acc, row) => ({
        subsidy: acc.subsidy + row.subsidy,
        selfPay: acc.selfPay + row.selfPay,
        donate: acc.donate + row.donate,
        profit: acc.profit + row.profit,
        total: acc.total + row.total,
      }),
      { subsidy: 0, selfPay: 0, donate: 0, profit: 0, total: 0 }
    );
    rows.push({
      key: `${side}-total`,
      spacer: false,
      side,
      totalRow: true,
      gubun: { text: "", span: 1 },
      lname: { text: "", span: 1 },
      mname: { text: "", span: 1 },
      sname: `${label} 합계`,
      subsidy: sum.subsidy,
      selfPay: sum.selfPay,
      donate: sum.donate,
      profit: sum.profit,
      total: sum.total,
      item: "",
      basis: "",
    });
    return rows;
  };

  const income = makeSide("in");
  const expense = makeSide("out");
  if (income.length && expense.length) return [...income, blankSheetLine("sheet-gap"), ...expense];
  return [...income, ...expense];
}

function buildHopeSheetLines(): HopeSheetLine[] {
  commitProcessedToAll();
  const grouped = new Map<string, HopeSheetLine & { bases: string[] }>();
  allProcessedData.value.forEach((item, index) => {
    const amount = getExactAmount(item);
    if (!amount) return;
    const hope = (item.fc_hope_scode || "").trim() || item.fc_scode;
    const fundCode = (item.fc_funding || "").trim() || "06";
    const key = `${item.fc_trcode}|${hope}|${fundCode}`;
    const memo = (item.description || "").trim();
    const price = parseWon(item.unit_price);
    const person = parseWon(item.count);
    const month = parseWon(item.months);
    const basis = `${memo} : ${price} x ${person} x ${month} = ${amount.toLocaleString("ko-KR")}`;
    const named = getFundingDisplayName(fundCode);
    const fundName = named && named !== fundCode ? named : "수익사업";
    const prev = grouped.get(key);
    if (!prev) {
      grouped.set(key, {
        key: `${key}-${index}`,
        bizCode: "",
        bizName: props.bizGubun || "",
        accountCode: hope,
        accountName: item.fc_sname || "",
        fundCode,
        fundName,
        amount,
        national: "",
        sido: "",
        sigungu: "",
        basis,
        bases: [basis],
      });
      return;
    }
    prev.amount += amount;
    if (!prev.bases.includes(basis)) prev.bases.push(basis);
  });
  return Array.from(grouped.values()).map((row) => ({
    key: row.key,
    bizCode: row.bizCode,
    bizName: row.bizName,
    accountCode: row.accountCode,
    accountName: row.accountName,
    fundCode: row.fundCode,
    fundName: row.fundName,
    amount: row.amount,
    national: row.national,
    sido: row.sido,
    sigungu: row.sigungu,
    basis: row.bases.join("\n"),
  }));
}

function requestSavedExport(kind: "excel" | "hope" | "salary") {
  if (!hasMemberCode.value) {
    showSnackbar("기관 정보(memberCode)가 없습니다.", "error");
    return;
  }
  if (kind === "excel") {
    openBudgetSheetPreview();
    return;
  }
  if (kind === "hope") {
    openHopeSheetPreview();
    return;
  }
  savedExportKind.value = kind;
  if (budgetDirty.value) {
    showSavedExportDialog.value = true;
    return;
  }
  void runSavedExport(kind);
}

function openBudgetSheetPreview() {
  budgetSheetLines.value = buildBudgetSheetLines();
  showBudgetSheet.value = true;
}

function openHopeSheetPreview() {
  hopeSheetLines.value = buildHopeSheetLines();
  showHopeSheet.value = true;
}

async function downloadHopeSaved() {
  showHopeSheet.value = false;
  await downloadHopeExcelFile();
}

async function downloadHopeAfterSave() {
  const saved = await saveAllData();
  if (!saved) return;
  showHopeSheet.value = false;
  await downloadHopeExcelFile();
}

async function downloadSheetSaved() {
  showBudgetSheet.value = false;
  await downloadBudgetExcelFile();
}

async function downloadSheetAfterSave() {
  const saved = await saveAllData();
  if (!saved) return;
  showBudgetSheet.value = false;
  await downloadBudgetExcelFile();
}

async function exportSavedAnyway() {
  const kind = savedExportKind.value;
  showSavedExportDialog.value = false;
  await runSavedExport(kind);
}

async function saveThenExport() {
  const kind = savedExportKind.value;
  showSavedExportDialog.value = false;
  const saved = await saveAllData();
  if (saved) await runSavedExport(kind);
}

async function runSavedExport(kind: "excel" | "hope" | "salary") {
  if (kind === "hope") await downloadHopeExcelFile();
  else if (kind === "salary") await openEmployeeSalary();
  else await downloadBudgetExcelFile();
}

async function downloadBudgetExcelFile() {
  if (!hasMemberCode.value) {
    showSnackbar("기관 정보(memberCode)가 없습니다.", "error");
    return;
  }
  try {
    const res = await budgetApi.downloadExcel({
      memberCode: memberCodeStr.value,
      year: searchParams.value.lebYear,
      budClass: props.budgetClass || "",
      bizGubun: props.bizGubun || "",
    });
    const bud = props.budgetClass || "";
    const label = !bud || bud === "0" ? "본예산" : `${bud}차추경`;
    downloadBlob(res.data as Blob, `예산_${props.bizGubun || ""}_${searchParams.value.lebYear}_${label}.xlsx`);
  } catch (error: unknown) {
    const err = error as { response?: { data?: Blob | { message?: string } } };
    let msg = "Excel 다운로드에 실패했습니다.";
    if (err.response?.data instanceof Blob) {
      try {
        const t = JSON.parse(await err.response.data.text());
        msg = t.message || msg;
      } catch { /* ignore */ }
    }
    showSnackbar(msg, "error");
  }
}

async function downloadHopeExcelFile() {
  if (!hasMemberCode.value) {
    showSnackbar("기관 정보(memberCode)가 없습니다.", "error");
    return;
  }
  try {
    const res = await budgetApi.downloadHopeExcel({
      memberCode: memberCodeStr.value,
      year: searchParams.value.lebYear,
      budClass: props.budgetClass || "",
      bizGubun: props.bizGubun || "",
    });
    downloadBlob(res.data as Blob, `예산_희망업로드_${searchParams.value.lebYear}.xlsx`);
  } catch {
    showSnackbar("희망업로드 Excel 다운로드에 실패했습니다.", "error");
  }
}

async function downloadSalaryExcel() {
  if (!hasMemberCode.value) {
    showSnackbar("기관 정보(memberCode)가 없습니다.", "error");
    return;
  }
  try {
    const res = await budgetApi.downloadEmployeeSalaryExcel({
      memberCode: memberCodeStr.value,
      year: searchParams.value.lebYear,
      budClass: props.budgetClass || "",
      bizGubun: props.bizGubun || "",
    });
    downloadBlob(res.data as Blob, `${searchParams.value.lebYear}년 임직원보수일람표.xlsx`);
  } catch {
    showSnackbar("임직원 보수 일람표 다운로드에 실패했습니다.", "error");
  }
}

async function openEmployeeSalary() {
  if (!hasMemberCode.value) {
    showSnackbar("기관 정보(memberCode)가 없습니다.", "error");
    return;
  }
  try {
    const res = await budgetApi.getEmployeeSalary({
      memberCode: memberCodeStr.value,
      year: searchParams.value.lebYear,
      budClass: props.budgetClass || "",
      bizGubun: props.bizGubun || "",
    });
    salaryRows.value = res.data?.data || [];
    showSalaryDialog.value = true;
  } catch {
    showSnackbar("임직원 보수 조회에 실패했습니다.", "error");
  }
}

function blankFinanceRow(fc: FinanceCode): FinanceCode {
  const isSalary = SALARY_SCODES.includes(fc.fc_scode);
  const isRevenuePair = fc.fc_trcode === "IN" && REVENUE_PAIR_SCODES.includes(fc.fc_scode);
  return {
    ...fc,
    hb_idx: undefined,
    hb_pay_rel: undefined,
    hb_pay_rel_manual: "N",
    hb_revenue_rel: undefined,
    description: "",
    unit_price: "",
    count: "",
    months: "",
    budget_amount: "",
    isSalaryItem: isSalary,
    isLinkedItem: false,
    isRevenuePairItem: isRevenuePair,
    suga_rank: isRevenuePair ? "" : undefined,
    suga_times: isRevenuePair ? "" : undefined,
  };
}

function wizardLineToRow(fc: FinanceCode, line: WizardLine): FinanceCode {
  const row = blankFinanceRow(fc);
  const isRevenuePair = row.fc_trcode === "IN" && REVENUE_PAIR_SCODES.includes(row.fc_scode) && Boolean(line.revenueRel);
  row.description = line.description;
  row.unit_price = line.unitPrice > 0 ? line.unitPrice.toLocaleString() : "0";
  row.count = line.count > 0 ? line.count.toLocaleString() : "";
  row.months = line.months > 0 ? String(line.months) : "";
  row.hb_pay_rel = line.payRel;
  row.hb_revenue_rel = line.revenueRel;
  if (line.funding) {
    row.fc_funding = line.funding;
    row.fc_funding_name = getFundingDisplayName(line.funding) || row.fc_funding_name;
  }
  row.isSalaryItem = Boolean(line.isSalary);
  row.isLinkedItem = Boolean(line.isLinked);
  row.isRevenuePairItem = isRevenuePair;
  if (isRevenuePair) {
    row.suga_rank = line.sugaRank || "";
    row.suga_times = line.sugaTimes || revenuePairDefaultTimes.value;
  }
  return row;
}

/** 마법사 결과를 현재 연도·차수 표에 올린다. 저장 버튼을 눌러야 서버에 남는다. */
async function applyWizardLines(lines: WizardLine[]) {
  if (selectedTrcode.value !== "ALL") {
    suppressBudgetFetch.value = true;
    selectedTrcode.value = "ALL";
    suppressBudgetFetch.value = false;
    await fetchFinanceCodes(true);
  }
  const catalog = financeCatalog.value.length
    ? financeCatalog.value
    : financeCodes.value.filter((fc, index, list) =>
        list.findIndex((row) => row.fc_trcode === fc.fc_trcode && row.fc_scode === fc.fc_scode) === index
      );
  if (!catalog.length) {
    showSnackbar("재무코드를 아직 불러오지 못했습니다.", "error");
    return;
  }
  const oldIds = financeCodes.value
    .map((row) => row.hb_idx)
    .filter((id): id is number => typeof id === "number" && id > 0);
  if (oldIds.length) {
    pendingDeleteIds.value = Array.from(new Set([...pendingDeleteIds.value, ...oldIds]));
  }
  const grouped = new Map<string, WizardLine[]>();
  lines.forEach((line) => {
    const key = `${line.trcode}-${line.scode}`;
    const bucket = grouped.get(key) || [];
    bucket.push(line);
    grouped.set(key, bucket);
  });
  const next: FinanceCode[] = [];
  catalog.forEach((fc) => {
    const bucket = grouped.get(`${fc.fc_trcode}-${fc.fc_scode}`);
    if (!bucket?.length) {
      next.push(blankFinanceRow(fc));
      return;
    }
    bucket.forEach((line) => next.push(wizardLineToRow(fc, line)));
  });
  financeCodes.value = next;
  allProcessedData.value = [];
  processedData.value = [];
  processFinanceCodes();
  showBudgetWizard.value = false;
  openSaveDialog(
    lines.length
      ? "마법사 내용을 예산 표에 적용했습니다. 저장해야 서버에 남습니다."
      : "예산 표의 금액을 비웠습니다. 저장해야 서버에 남습니다."
  );
}

// 관(L코드)별 예산액 합계 계산 (실시간)
const getLcodeBudgetSum = (item: ProcessedFinanceCode) => {
  const lcodeKey = `${item.fc_trcode}-${item.fc_lcode}`;
  const sameRows = processedData.value.filter(
    (row) => `${row.fc_trcode}-${row.fc_lcode}` === lcodeKey
  );
  
  const total = sameRows.reduce((sum, row) => {
    return sum + getExactAmount(row);
  }, 0);
  
  return total;
};

// 항(M코드)별 예산액 합계 계산 (실시간)
const getMcodeBudgetSum = (item: ProcessedFinanceCode) => {
  const mcodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}`;
  const sameRows = processedData.value.filter(
    (row) => `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}` === mcodeKey
  );
  
  const total = sameRows.reduce((sum, row) => {
    return sum + getExactAmount(row);
  }, 0);
  
  return total;
};

// 목(S코드)별 예산액 합계 계산 (실시간)
const getScodeBudgetSum = (item: ProcessedFinanceCode) => {
  const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;
  const sameRows = processedData.value.filter(
    (row) => `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}-${row.fc_scode}` === scodeKey
  );
  
  const total = sameRows.reduce((sum, row) => {
    return sum + getExactAmount(row);
  }, 0);
  
  return total;
};

// rowspan만 재계산하는 함수 (function 선언: processFinanceCodes 등에서 먼저 참조 가능)
function recalculateRowspans() {
  const data = processedData.value;
  
  // rowspan 카운트
  const trcodeGroups: { [key: string]: number } = {};
  const lcodeGroups: { [key: string]: number } = {};
  const mcodeGroups: { [key: string]: number } = {};
  const scodeGroups: { [key: string]: number } = {};

  data.forEach((item) => {
    const trcodeKey = `${item.fc_trcode}`;
    const lcodeKey = `${item.fc_trcode}-${item.fc_lcode}`;
    const mcodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}`;
    const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;

    trcodeGroups[trcodeKey] = (trcodeGroups[trcodeKey] || 0) + 1;
    lcodeGroups[lcodeKey] = (lcodeGroups[lcodeKey] || 0) + 1;
    mcodeGroups[mcodeKey] = (mcodeGroups[mcodeKey] || 0) + 1;
    scodeGroups[scodeKey] = (scodeGroups[scodeKey] || 0) + 1;
  });

  // 표시 여부 및 rowspan 업데이트
  let prevTrcode = "";
  let prevLcode = "";
  let prevMcode = "";
  let prevScode = "";
  let trcodeCount = 0;
  let lcodeCount = 0;
  let mcodeCount = 0;
  let scodeCount = 0;

  data.forEach((item) => {
    const trcodeKey = `${item.fc_trcode}`;
    const lcodeKey = `${item.fc_trcode}-${item.fc_lcode}`;
    const mcodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}`;
    const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;

    const showTrcode = item.fc_trcode !== prevTrcode;
    const showLcode = lcodeKey !== `${prevTrcode}-${prevLcode}`;
    const showMcode = mcodeKey !== `${prevTrcode}-${prevLcode}-${prevMcode}`;
    const showScode = scodeKey !== `${prevTrcode}-${prevLcode}-${prevMcode}-${prevScode}`;

    if (showTrcode) {
      prevTrcode = item.fc_trcode;
      trcodeCount = 0;
    }

    if (showLcode) {
      prevLcode = item.fc_lcode;
      lcodeCount = 0;
    }

    if (showMcode) {
      prevMcode = item.fc_mcode;
      mcodeCount = 0;
    }

    if (showScode) {
      prevScode = item.fc_scode;
      scodeCount = 0;
    }

    item.showTrcode = trcodeCount === 0;
    item.showLcode = lcodeCount === 0;
    item.showMcode = mcodeCount === 0;
    item.showScode = scodeCount === 0;
    item.trcodeRowspan = trcodeGroups[trcodeKey];
    item.lcodeRowspan = lcodeGroups[lcodeKey];
    item.mcodeRowspan = mcodeGroups[mcodeKey];
    item.scodeRowspan = scodeGroups[scodeKey];

    trcodeCount++;
    lcodeCount++;
    mcodeCount++;
    scodeCount++;
  });
}

/** 현재 탭에 맞게 표시용 processedData를 갱신하고 rowspan을 맞춘다 */
function refreshDisplayByTrcode() {
  const full = allProcessedData.value;
  processedData.value =
    selectedTrcode.value === "ALL"
      ? full
      : full.filter((row) => row.fc_trcode === selectedTrcode.value);
  recalculateRowspans();
}

// 급여 scode에 매칭되는 사회보험/퇴직금 scode 가져오기
const getLinkedScodes = (salaryScode: string): { insurance: string; retirement: string } | null => {
  if (salaryScode === '1111') {
    return { insurance: '1151', retirement: '1141' }; // 직접비
  } else if (salaryScode === '1112') {
    return { insurance: '1152', retirement: '1142' }; // 간접비
  }
  return null;
};

// 본인부담금(112) ↔ 장기요양급여수입(611) 짝 scode 가져오기
const getRevenuePairScode = (scode: string): string | null => {
  if (scode === '112') return '611';
  if (scode === '611') return '112';
  return null;
};

// 수입 쌍 연결 키 생성 (고유 키)
const generateRevenueRelKey = (): string => {
  return `rev_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// scode로 해당하는 financeCode 찾기
const findFinanceCodeByScode = (trcode: string, scode: string): FinanceCode | undefined => {
  return financeCodes.value.find(fc => fc.fc_trcode === trcode && fc.fc_scode === scode);
};

// scode 그룹의 마지막 인덱스 찾기 헬퍼 함수
const findLastIndexOfScodeGroup = (scodeKey: string, startIndex: number = 0): number => {
  let lastIndex = -1;
  for (let i = startIndex; i < processedData.value.length; i++) {
    const row = processedData.value[i];
    const rowScodeKey = `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}-${row.fc_scode}`;
    if (rowScodeKey === scodeKey) {
      lastIndex = i;
    }
  }
  return lastIndex;
};

// 행 추가 (화면에만 추가, 저장 시 DB INSERT)
// 급여 scode인 경우 사회보험/퇴직금도 함께 생성하고 같은 pay_rel 키로 연결
const addRow = (index: number) => {
  const currentItem = processedData.value[index];
  const isSalaryScode = SALARY_SCODES.includes(currentItem.fc_scode);
  
  if (isSalaryScode) {
    // 급여 scode인 경우: 급여 + 사회보험 + 퇴직금을 함께 생성
    const payRelKey = generatePayRelKey();
    const linkedScodes = getLinkedScodes(currentItem.fc_scode);
    
    if (!linkedScodes) {
      console.error("연결 scode를 찾을 수 없습니다.");
      return;
    }
    
    // 연결된 financeCode 찾기
    const insuranceFinanceCode = findFinanceCodeByScode('OUT', linkedScodes.insurance);
    const retirementFinanceCode = findFinanceCodeByScode('OUT', linkedScodes.retirement);
    
    // 급여 행 생성 (hb_idx 없음 - 저장 시 INSERT)
    const salaryRow: ProcessedFinanceCode = {
      ...currentItem,
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: payRelKey,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: undefined,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: true,
      isLinkedItem: false,
      isRevenuePairItem: false,
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    // 사회보험 행 생성
    const insuranceRow: ProcessedFinanceCode = {
      fc_idx: insuranceFinanceCode?.fc_idx || 0,
      fc_trcode: 'OUT',
      fc_trname: '세출',
      fc_year: currentItem.fc_year,
      fc_lcode: insuranceFinanceCode?.fc_lcode || '',
      fc_lname: insuranceFinanceCode?.fc_lname || '',
      fc_mcode: insuranceFinanceCode?.fc_mcode || '',
      fc_mname: insuranceFinanceCode?.fc_mname || '',
      fc_scode: linkedScodes.insurance,
      fc_sname: insuranceFinanceCode?.fc_sname || '사회보험부담금',
      fc_funding: insuranceFinanceCode?.fc_funding || '',
      fc_funding_name: insuranceFinanceCode?.fc_funding_name || '',
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: payRelKey,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: undefined,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: false,
      isLinkedItem: true,
      isRevenuePairItem: false,
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    // 퇴직금 행 생성
    const retirementRow: ProcessedFinanceCode = {
      fc_idx: retirementFinanceCode?.fc_idx || 0,
      fc_trcode: 'OUT',
      fc_trname: '세출',
      fc_year: currentItem.fc_year,
      fc_lcode: retirementFinanceCode?.fc_lcode || '',
      fc_lname: retirementFinanceCode?.fc_lname || '',
      fc_mcode: retirementFinanceCode?.fc_mcode || '',
      fc_mname: retirementFinanceCode?.fc_mname || '',
      fc_scode: linkedScodes.retirement,
      fc_sname: retirementFinanceCode?.fc_sname || '퇴직금',
      fc_funding: retirementFinanceCode?.fc_funding || '',
      fc_funding_name: retirementFinanceCode?.fc_funding_name || '',
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: payRelKey,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: undefined,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: false,
      isLinkedItem: true,
      isRevenuePairItem: false,
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    // 급여 행 추가
    const salaryScodeKey = `${salaryRow.fc_trcode}-${salaryRow.fc_lcode}-${salaryRow.fc_mcode}-${salaryRow.fc_scode}`;
    const salaryInsertIndex = findLastIndexOfScodeGroup(salaryScodeKey, index);
    processedData.value.splice((salaryInsertIndex >= 0 ? salaryInsertIndex : index) + 1, 0, salaryRow);
    
    // 사회보험 행 추가
    const insuranceScodeKey = `${insuranceRow.fc_trcode}-${insuranceRow.fc_lcode}-${insuranceRow.fc_mcode}-${insuranceRow.fc_scode}`;
    const insuranceInsertIndex = findLastIndexOfScodeGroup(insuranceScodeKey);
    if (insuranceInsertIndex >= 0) {
      processedData.value.splice(insuranceInsertIndex + 1, 0, insuranceRow);
    }
    
    // 퇴직금 행 추가
    const retirementScodeKey = `${retirementRow.fc_trcode}-${retirementRow.fc_lcode}-${retirementRow.fc_mcode}-${retirementRow.fc_scode}`;
    const retirementInsertIndex = findLastIndexOfScodeGroup(retirementScodeKey);
    if (retirementInsertIndex >= 0) {
      processedData.value.splice(retirementInsertIndex + 1, 0, retirementRow);
    }
    
    recalculateRowspans();
    commitProcessedToAll();
    
  } else if (currentItem.fc_trcode === 'IN' && REVENUE_PAIR_SCODES.includes(currentItem.fc_scode)) {
    // 본인부담금(112) 또는 장기요양급여수입(611) 추가 시 짝도 함께 생성 (세입만)
    const revenueRelKey = generateRevenueRelKey();
    const pairScode = getRevenuePairScode(currentItem.fc_scode);
    
    if (!pairScode) {
      console.error("짝 scode를 찾을 수 없습니다.");
      return;
    }
    
    const pairFinanceCode = findFinanceCodeByScode('IN', pairScode);
    
    // 현재 항목 행 생성
    const currentRow: ProcessedFinanceCode = {
      ...currentItem,
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: undefined,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: revenueRelKey,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: false,
      isLinkedItem: false,
      isRevenuePairItem: true,
      suga_rank: "",
      suga_times: "",
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    // 짝 항목 행 생성
    const pairRow: ProcessedFinanceCode = {
      fc_idx: pairFinanceCode?.fc_idx || 0,
      fc_trcode: 'IN',
      fc_trname: '세입',
      fc_year: currentItem.fc_year,
      fc_lcode: pairFinanceCode?.fc_lcode || '',
      fc_lname: pairFinanceCode?.fc_lname || '',
      fc_mcode: pairFinanceCode?.fc_mcode || '',
      fc_mname: pairFinanceCode?.fc_mname || '',
      fc_scode: pairScode,
      fc_sname: pairFinanceCode?.fc_sname || (pairScode === '112' ? '본인부담금수입' : '장기요양급여수입'),
      fc_funding: pairFinanceCode?.fc_funding || '',
      fc_funding_name: pairFinanceCode?.fc_funding_name || '',
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: undefined,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: revenueRelKey,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: false,
      isLinkedItem: false,
      isRevenuePairItem: true,
      suga_rank: "",
      suga_times: "",
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    // 현재 항목 행 추가
    const currentScodeKey = `${currentRow.fc_trcode}-${currentRow.fc_lcode}-${currentRow.fc_mcode}-${currentRow.fc_scode}`;
    const currentInsertIndex = findLastIndexOfScodeGroup(currentScodeKey, index);
    processedData.value.splice((currentInsertIndex >= 0 ? currentInsertIndex : index) + 1, 0, currentRow);
    
    // 짝 항목 행 추가
    const pairScodeKey = `${pairRow.fc_trcode}-${pairRow.fc_lcode}-${pairRow.fc_mcode}-${pairRow.fc_scode}`;
    const pairInsertIndex = findLastIndexOfScodeGroup(pairScodeKey);
    if (pairInsertIndex >= 0) {
      processedData.value.splice(pairInsertIndex + 1, 0, pairRow);
    }
    
    recalculateRowspans();
    commitProcessedToAll();
  } else {
    // 일반 행 추가
    const scodeKey = `${currentItem.fc_trcode}-${currentItem.fc_lcode}-${currentItem.fc_mcode}-${currentItem.fc_scode}`;
    const insertIndex = findLastIndexOfScodeGroup(scodeKey, index);
    
    const newRow: ProcessedFinanceCode = {
      ...currentItem,
      hb_idx: undefined, // 저장 시 INSERT
      hb_pay_rel: undefined,
      hb_pay_rel_manual: 'N',
      hb_revenue_rel: undefined,
      budget_amount: "",
      description: "",
      unit_price: "",
      count: "",
      months: "",
      isAddedRow: true,
      isSalaryItem: false,
      isLinkedItem: false,
      isRevenuePairItem: false,
      showTrcode: false,
      showLcode: false,
      showMcode: false,
      showScode: false,
      trcodeRowspan: 0,
      lcodeRowspan: 0,
      mcodeRowspan: 0,
      scodeRowspan: 0,
    };
    
    processedData.value.splice((insertIndex >= 0 ? insertIndex : index) + 1, 0, newRow);
    recalculateRowspans();
    commitProcessedToAll();
  }
};

// 같은 목 코드를 가진 행의 개수 확인
const getSameScodeRowCount = (item: ProcessedFinanceCode) => {
  const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;
  return processedData.value.filter(
    (row) => `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}-${row.fc_scode}` === scodeKey
  ).length;
};

// 삭제 가능 여부 확인 (연결 항목만 개별 삭제 불가)
const canDeleteRow = (item: ProcessedFinanceCode) => {
  // 연결된 항목(사회보험/퇴직금)은 개별 삭제 불가 (급여와 함께 삭제됨)
  if (item.isLinkedItem) {
    return false;
  }
  // 나머지 항목은 삭제 가능 (마지막 행이면 데이터만 초기화)
  return true;
};

// 삭제 대기 목록에 추가 (저장 시 실제 DB 삭제)
const addToPendingDelete = (hbIdx: number) => {
  if (hbIdx && !pendingDeleteIds.value.includes(hbIdx)) {
    pendingDeleteIds.value.push(hbIdx);
  }
};

// 행 데이터 초기화 (마지막 행일 때 사용)
const clearRowData = (item: ProcessedFinanceCode) => {
  // 삭제 대기 목록에 추가
  if (item.hb_idx) {
    addToPendingDelete(item.hb_idx);
  }
  // 데이터 초기화
  item.hb_idx = undefined;
  item.description = '';
  item.unit_price = '';
  item.count = '';
  item.months = '';
  item.hb_pay_rel = undefined;
  item.hb_pay_rel_manual = 'N';
  item.hb_revenue_rel = undefined;
  item.suga_rank = item.isRevenuePairItem ? '' : undefined;
  item.suga_times = item.isRevenuePairItem ? '' : undefined;
};

// 급여 항목의 같은 scode 내 순서 인덱스 가져오기
const getSalaryIndexInScode = (item: ProcessedFinanceCode): number => {
  if (!item.isSalaryItem) return -1;
  const scodeKey = `${item.fc_trcode}-${item.fc_lcode}-${item.fc_mcode}-${item.fc_scode}`;
  const sameScodeItems = processedData.value.filter(
    row => `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}-${row.fc_scode}` === scodeKey && row.isSalaryItem
  );
  return sameScodeItems.findIndex(row => row === item);
};

// 순서 기반으로 연결된 사회보험/퇴직금 항목 찾기
const findLinkedItemsByOrder = (salaryItem: ProcessedFinanceCode): ProcessedFinanceCode[] => {
  const linkedItems: ProcessedFinanceCode[] = [];
  const salaryIndex = getSalaryIndexInScode(salaryItem);
  if (salaryIndex < 0) return linkedItems;
  
  const linkedScodes = getLinkedScodes(salaryItem.fc_scode);
  if (!linkedScodes) return linkedItems;
  
  // 사회보험 scode의 같은 순서 항목 찾기
  const insuranceScodeKey = `OUT-${linkedScodes.insurance}`;
  let insuranceCount = 0;
  for (const row of processedData.value) {
    if (row.fc_trcode === 'OUT' && row.fc_scode === linkedScodes.insurance && row.isLinkedItem) {
      if (insuranceCount === salaryIndex) {
        linkedItems.push(row);
        break;
      }
      insuranceCount++;
    }
  }
  
  // 퇴직금 scode의 같은 순서 항목 찾기
  let retirementCount = 0;
  for (const row of processedData.value) {
    if (row.fc_trcode === 'OUT' && row.fc_scode === linkedScodes.retirement && row.isLinkedItem) {
      if (retirementCount === salaryIndex) {
        linkedItems.push(row);
        break;
      }
      retirementCount++;
    }
  }
  
  return linkedItems;
};

// 순서 기반으로 수입 쌍 짝궁 찾기 (기존 getRevenuePairItem 사용)
// getRevenuePairItem 함수가 이미 순서 기반으로 동작함

// 행 삭제 또는 데이터 초기화 (저장 시 실제 DB 반영)
// - 마지막 행이면 데이터만 초기화
// - 급여 삭제 시 순서 기반으로 사회보험/퇴직금도 함께 삭제
// - 수입 쌍 삭제 시 순서 기반으로 짝도 함께 삭제
const deleteRow = (index: number) => {
  const item = processedData.value[index];
  
  // 연결 항목은 개별 삭제 불가
  if (item.isLinkedItem) {
    return;
  }
  
  const isLastRow = getSameScodeRowCount(item) === 1;
  
  // 1. 급여 항목인 경우: 연결된 사회보험/퇴직금도 함께 처리
  if (item.isSalaryItem) {
    // hb_pay_rel이 있으면 같은 키로 찾기, 없으면 순서 기반으로 찾기
    let linkedItems: ProcessedFinanceCode[] = [];
    
    if (item.hb_pay_rel) {
      linkedItems = processedData.value.filter(
        row => row.hb_pay_rel === item.hb_pay_rel && row !== item
      );
    } else {
      linkedItems = findLinkedItemsByOrder(item);
    }
    
    if (isLastRow) {
      // 마지막 행이면 데이터만 초기화
      clearRowData(item);
      // 연결된 항목들도 데이터 초기화
      for (const linkedItem of linkedItems) {
        const linkedIsLast = getSameScodeRowCount(linkedItem) === 1;
        if (linkedIsLast) {
          clearRowData(linkedItem);
        } else {
          // 연결 항목의 scode에 여러 행이 있으면 삭제
          if (linkedItem.hb_idx) {
            addToPendingDelete(linkedItem.hb_idx);
          }
          const linkedIndex = processedData.value.indexOf(linkedItem);
          if (linkedIndex >= 0) {
            processedData.value.splice(linkedIndex, 1);
          }
        }
      }
    } else {
      // 마지막 행이 아니면 삭제
      // 연결된 항목들 먼저 삭제 (역순으로)
      for (const linkedItem of linkedItems) {
        if (linkedItem.hb_idx) {
          addToPendingDelete(linkedItem.hb_idx);
        }
        const linkedIndex = processedData.value.indexOf(linkedItem);
        if (linkedIndex >= 0) {
          processedData.value.splice(linkedIndex, 1);
        }
      }
      // 급여 항목 삭제
      if (item.hb_idx) {
        addToPendingDelete(item.hb_idx);
      }
      // 인덱스가 변경되었을 수 있으므로 다시 찾기
      const currentIndex = processedData.value.indexOf(item);
      if (currentIndex >= 0) {
        processedData.value.splice(currentIndex, 1);
      }
    }
  }
  // 2. 수입 쌍(112/611) 항목인 경우: 짝도 함께 처리
  else if (isRevenuePairItem(item)) {
    // 순서 기반으로 짝 찾기
    const pairItem = getRevenuePairItem(item);
    
    if (isLastRow) {
      // 마지막 행이면 데이터만 초기화
      clearRowData(item);
      // 짝 항목도 처리
      if (pairItem) {
        const pairIsLast = getSameScodeRowCount(pairItem) === 1;
        if (pairIsLast) {
          clearRowData(pairItem);
        } else {
          if (pairItem.hb_idx) {
            addToPendingDelete(pairItem.hb_idx);
          }
          const pairIndex = processedData.value.indexOf(pairItem);
          if (pairIndex >= 0) {
            processedData.value.splice(pairIndex, 1);
          }
        }
      }
    } else {
      // 마지막 행이 아니면 삭제
      // 짝 항목 먼저 삭제
      if (pairItem) {
        if (pairItem.hb_idx) {
          addToPendingDelete(pairItem.hb_idx);
        }
        const pairIndex = processedData.value.indexOf(pairItem);
        if (pairIndex >= 0) {
          processedData.value.splice(pairIndex, 1);
        }
      }
      // 현재 항목 삭제
      if (item.hb_idx) {
        addToPendingDelete(item.hb_idx);
      }
      const currentIndex = processedData.value.indexOf(item);
      if (currentIndex >= 0) {
        processedData.value.splice(currentIndex, 1);
      }
    }
  }
  // 3. 일반 항목
  else {
    if (isLastRow) {
      // 마지막 행이면 데이터만 초기화
      clearRowData(item);
    } else {
      // 마지막 행이 아니면 삭제
      if (item.hb_idx) {
        addToPendingDelete(item.hb_idx);
      }
      processedData.value.splice(index, 1);
    }
  }
  
  // rowspan 재계산 후 전체 목록에 반영(탭 필터 상태에서도 리모컨 합계 유지)
  recalculateRowspans();
  commitProcessedToAll();
};

// 추경차수 라벨
const budgetClassLabel = computed(() => {
  const budClass = props.budgetClass || '';
  if (!budClass || budClass === '0') return '본예산';
  return `${budClass}차 추경`;
});

// 추경차수 스타일
const budgetClassStyle = computed(() => {
  const budClass = props.budgetClass || '';
  if (!budClass || budClass === '0') return 'primary';
  return 'warning';
});

// 추경차수 아이콘
const budgetClassIcon = computed(() => {
  const budClass = props.budgetClass || '';
  if (!budClass || budClass === '0') return 'mdi-file-document-outline';
  return 'mdi-file-document-edit-outline';
});

// 연도 범위
const currentSystemYear = new Date().getFullYear();
const minYear = currentSystemYear - 2;
const maxYear = currentSystemYear + 1;

// 연도 변경 함수
const changeYear = async (delta: number) => {
  const newYear = parseInt(searchParams.value.lebYear) + delta;
  if (newYear < minYear || newYear > maxYear) return;
  if (!(await confirmDiscard())) return;
  // 조회와 year-change emit은 watcher가 단일로 처리 (여기서 직접 부르면 이중 호출)
  searchParams.value.lebYear = newYear.toString();
};

// 금액 포맷팅 함수
const formatMoney = (amount: number): string => {
  return new Intl.NumberFormat('ko-KR').format(amount);
};

// 세입 합계 (탭 필터와 무관 — 리모컨은 항상 전체 세입·세출)
const totalRevenue = computed(() => {
  let total = 0;
  allProcessedData.value.forEach(item => {
    if (item.fc_trcode === 'IN') {
      total += getExactAmount(item);
    }
  });
  return total;
});

// 세출 합계 (탭 필터와 무관)
const totalExpense = computed(() => {
  let total = 0;
  allProcessedData.value.forEach(item => {
    if (item.fc_trcode === 'OUT') {
      total += getExactAmount(item);
    }
  });
  return total;
});

// 세입−세출 차액 (타 시스템 budSumList.in_total_budget_money − out_total_budget_money 와 동일)
const budgetDifference = computed(() => totalRevenue.value - totalExpense.value);

/** 합계 표시용 객체 (다른 프로젝트 budSumList 필드명 호환) */
const budSumList = computed(() => ({
  in_예산총액_총합: formatMoney(totalRevenue.value),
  out_예산총액_총합: formatMoney(totalExpense.value),
  in_total_budget_money: totalRevenue.value,
  out_total_budget_money: totalExpense.value,
}));

function addComma(amount: number): string {
  const n = Number(amount);
  if (Number.isNaN(n)) return "0";
  return new Intl.NumberFormat("ko-KR").format(n);
}

// 기준 인건비 비율 (props에서 가져오거나 기본값 60%)
const standardLaborCostRate = computed(() => {
  return effectiveTaxData.value?.tr_labor_cost_rate || 60;
});

// 인건비 총액 계산 (직접비: 급여1111 + 사회보험1151 + 퇴직금1141)
const totalLaborCost = computed(() => {
  let total = 0;
  allProcessedData.value.forEach(item => {
    // 직접비 급여, 사회보험, 퇴직금만 포함
    if (['1111', '1151', '1141'].includes(item.fc_scode)) {
      total += getExactAmount(item);
    }
  });
  return total;
});

// 인건비 비율 계산용 수입 (본인부담금112 + 장기요양급여611 + 가산금613)
const laborCostBaseRevenue = computed(() => {
  let total = 0;
  allProcessedData.value.forEach(item => {
    if (['112', '611', '613'].includes(item.fc_scode)) {
      total += getExactAmount(item);
    }
  });
  return total;
});

// 현재 인건비 비율
const currentLaborCostRate = computed(() => {
  if (laborCostBaseRevenue.value === 0) return 0;
  return (totalLaborCost.value / laborCostBaseRevenue.value) * 100;
});

// 인건비 비율 기준 미달 여부
const isLaborCostBelowStandard = computed(() => {
  return currentLaborCostRate.value < standardLaborCostRate.value;
});

/** 기준 대비 인건비율 비교 (>, =, < 표시용) */
const laborCompare = computed((): "none" | "eq" | "gt" | "lt" => {
  if (laborCostBaseRevenue.value <= 0) return "none";
  const c = currentLaborCostRate.value;
  const s = standardLaborCostRate.value;
  if (Math.abs(c - s) < 0.05) return "eq";
  if (c > s) return "gt";
  return "lt";
});

/** 인건비 비율 바(0~100 clamp) — 리모컨 미터 표시용 */
const laborBarPercent = computed(() =>
  Math.min(100, Math.max(0, currentLaborCostRate.value))
);
const laborStandardPercent = computed(() =>
  Math.min(100, Math.max(0, Number(standardLaborCostRate.value) || 0))
);

// 예산 삭제 함수
const deleteBudget = async () => {
  if (!hasMemberCode.value) {
    showSnackbar("기관 정보(memberCode)가 없습니다.", "error");
    return;
  }

  deleting.value = true;
  try {
    const response = await budgetApi.deleteYear({
      memberCode: memberCodeStr.value,
      year: searchParams.value.lebYear,
      budClass: props.budgetClass || "",
      bizGubun: props.bizGubun || "",
    });

    if (response.data.success) {
      showDeleteDialog.value = false;
      await fetchFinanceCodes();
      showSnackbar(`${searchParams.value.lebYear}년 ${budgetClassLabel.value} 예산이 삭제되었습니다.`, "success");
      if (props.useMemberPageLayout) {
        emit("budget-deleted");
      }
    } else {
      const msg = (response.data as { message?: string }).message || "알 수 없는 오류";
      showSnackbar(`예산 삭제 실패: ${msg}`, "error");
    }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string }; status?: number } };
    const st = err.response?.status;
    const msg = err.response?.data?.message || (error instanceof Error ? error.message : "알 수 없는 오류");
    if (st === 409) {
      showSnackbar(msg, "error");
    } else {
      showSnackbar(`예산 삭제 중 오류: ${msg}`, "error");
    }
    console.error("예산 삭제 오류:", error);
  } finally {
    deleting.value = false;
  }
};

// 급여 인풋 포커스 시 그룹 활성화
const handleSalaryFocus = (item: ProcessedFinanceCode) => {
  if (item.isSalaryItem && item.hb_pay_rel) {
    focusedPayRelGroup.value = item.hb_pay_rel;
  }
};

// 급여 인풋 블러 시 그룹 비활성화
const handleSalaryBlur = () => {
  focusedPayRelGroup.value = null;
};

// 본인부담금-장기요양급여 포커스 시 쌍 그룹 활성화 (순서 기반, 세입만)
const handleRevenuePairFocus = (item: ProcessedFinanceCode) => {
  if (isRevenuePairItem(item)) {
    focusedRevenuePairItem.value = item;
  }
};

// 본인부담금-장기요양급여 블러 시 쌍 그룹 비활성화
const handleRevenuePairBlur = () => {
  focusedRevenuePairItem.value = null;
};

// 본인부담금/장기요양급여 scode 확인 (세입에서만 유효)
const isRevenuePairScode = (scode: string, trcode?: string): boolean => {
  // trcode가 제공되면 세입(IN)인지 확인
  if (trcode && trcode !== 'IN') return false;
  return REVENUE_PAIR_SCODES.includes(scode);
};

// 항목이 수입 쌍인지 확인 (trcode도 함께 체크)
const isRevenuePairItem = (item: ProcessedFinanceCode): boolean => {
  return item.fc_trcode === 'IN' && REVENUE_PAIR_SCODES.includes(item.fc_scode);
};

const isBasicCopay = (item: ProcessedFinanceCode | null | undefined): boolean =>
  Boolean(item && String(item.description || "").includes("기초"));

const revenuePairMemo = (item: ProcessedFinanceCode, basic: boolean): string => {
  if (!item.suga_rank || !item.suga_times) return "";
  return `${item.suga_rank} X ${item.suga_times}${basic ? " 기초" : ""}`;
};

// 수가 선택에 따른 단가 계산
// basic: 기초수급자는 본인부담 0, 장기요양급여는 급여단가 전액
const calculateSugaUnitPrice = (item: ProcessedFinanceCode, basicOverride?: boolean): number => {
  const list = effectiveSugaList.value;
  if (!item.suga_rank || !item.suga_times || !list.length) return 0;

  const suga = list.find((s) => s.suga_rank === item.suga_rank);
  if (!suga) return 0;
  
  const times = Number(item.suga_times.replace(/,/g, '')) || 0;
  const basic = basicOverride ?? isBasicCopay(item);
  
  // 본인부담금(112): suga_bonin × 횟수
  // 장기요양급여수입(611): (suga_money - suga_bonin) × 횟수
  if (item.fc_scode === '112') {
    return basic ? 0 : suga.suga_bonin * times;
  } else if (item.fc_scode === '611') {
    return basic ? suga.suga_money * times : (suga.suga_money - suga.suga_bonin) * times;
  }
  
  return 0;
};

// 수가 선택 변경 시 처리 (본인부담금 ↔ 장기요양급여 자동 연동)
const handleSugaChange = (item: ProcessedFinanceCode) => {
  const pair = isRevenuePairItem(item) ? getRevenuePairItem(item) : null;
  const basic = isBasicCopay(item) || isBasicCopay(pair);
  const unitPrice = calculateSugaUnitPrice(item, basic);
  item.unit_price = unitPrice > 0 ? unitPrice.toLocaleString() : basic && item.fc_scode === "112" ? "0" : "";
  item.description = revenuePairMemo(item, basic);
  
  // 본인부담금(112) ↔ 장기요양급여(611) 자동 연동 (세입만)
  if (isRevenuePairItem(item)) {
    syncRevenuePairValues(item, 'suga_rank');
    syncRevenuePairValues(item, 'suga_times');
  }
};

// 수가 횟수 입력 변경 시 처리 (본인부담금 ↔ 장기요양급여 자동 연동)
const handleSugaTimesInput = (event: any, item: ProcessedFinanceCode) => {
  // 숫자만 허용
  const value = event.target.value.replace(/[^0-9]/g, '');
  item.suga_times = value;
  
  const pair = isRevenuePairItem(item) ? getRevenuePairItem(item) : null;
  const basic = isBasicCopay(item) || isBasicCopay(pair);
  const unitPrice = calculateSugaUnitPrice(item, basic);
  item.unit_price = unitPrice > 0 ? unitPrice.toLocaleString() : basic && item.fc_scode === "112" ? "0" : "";
  item.description = revenuePairMemo(item, basic);
  
  // 본인부담금(112) ↔ 장기요양급여(611) 자동 연동 (세입만)
  if (isRevenuePairItem(item)) {
    syncRevenuePairValues(item, 'suga_times');
  }
};

// 본인부담금(112) 항목들의 인덱스 배열 가져오기
const getRevenue112Items = (): ProcessedFinanceCode[] => {
  return processedData.value.filter(row => row.fc_scode === '112');
};

// 장기요양급여(611) 항목들의 인덱스 배열 가져오기
const getRevenue611Items = (): ProcessedFinanceCode[] => {
  return processedData.value.filter(row => row.fc_scode === '611');
};

// 본인부담금/장기요양급여 항목의 순서(인덱스) 가져오기
const getRevenuePairIndex = (item: ProcessedFinanceCode): number => {
  if (item.fc_scode === '112') {
    return getRevenue112Items().findIndex(row => row === item);
  } else if (item.fc_scode === '611') {
    return getRevenue611Items().findIndex(row => row === item);
  }
  return -1;
};

// 짝궁 항목 가져오기 (순서 기반, 세입만)
const getRevenuePairItem = (item: ProcessedFinanceCode): ProcessedFinanceCode | null => {
  if (!isRevenuePairItem(item)) return null;
  
  const index = getRevenuePairIndex(item);
  if (index < 0) return null;
  
  const pairItems = item.fc_scode === '112' ? getRevenue611Items() : getRevenue112Items();
  return pairItems[index] || null;
};

// 포커스된 수입 쌍 정보 저장 (항목 자체 참조)
const focusedRevenuePairItem = ref<ProcessedFinanceCode | null>(null);

// 해당 행이 현재 포커스된 수입 쌍의 짝궁인지 확인
const isRevenuePairHighlighted = (item: ProcessedFinanceCode): boolean => {
  if (!focusedRevenuePairItem.value) return false;
  if (!isRevenuePairItem(item)) return false;
  
  // 포커스된 항목의 짝궁인지 확인
  const pairItem = getRevenuePairItem(focusedRevenuePairItem.value);
  return pairItem === item;
};

// 본인부담금-장기요양급여 쌍 항목의 count, months, suga_rank, suga_times 연동
// 순서 기반으로 연동 (112 N번째 ↔ 611 N번째)
const syncRevenuePairValues = (item: ProcessedFinanceCode, field: 'count' | 'months' | 'suga_rank' | 'suga_times') => {
  // 본인부담금(112) 또는 장기요양급여(611)인지 확인 (세입만)
  if (!isRevenuePairItem(item)) return;
  
  // 순서 기반으로 짝궁 찾기
  const pairItem = getRevenuePairItem(item);
  if (!pairItem) return;
  
  const newValue = item[field];
  
  // @ts-ignore - dynamic field assignment
  pairItem[field] = newValue;
  
  // suga_rank나 suga_times가 변경되면 unit_price도 재계산
  if (field === 'suga_rank' || field === 'suga_times') {
    const basic = isBasicCopay(item) || isBasicCopay(pairItem);
    const unitPrice = calculateSugaUnitPrice(pairItem, basic);
    pairItem.unit_price = unitPrice > 0 ? unitPrice.toLocaleString() : basic && pairItem.fc_scode === "112" ? "0" : "";
    pairItem.description = revenuePairMemo(pairItem, basic);
  }
};

// 본인부담금-장기요양급여 입력 변경 핸들러
const handleRevenuePairInput = (event: any, item: ProcessedFinanceCode, field: string) => {
  // 기존 숫자 포맷팅 적용
  formatNumber(event, item, field);
  
  // 본인부담금/장기요양급여 항목이고 count나 months 변경 시 쌍 항목도 연동 (세입만)
  if (isRevenuePairItem(item) && (field === 'count' || field === 'months')) {
    syncRevenuePairValues(item, field as 'count' | 'months');
  }
};

// 급여 종류 변경 시 연결된 항목 description 업데이트 및 재계산
const handleSalaryTypeChange = async (salaryItem: ProcessedFinanceCode) => {
  if (!salaryItem.isSalaryItem) return;
  
  const salaryType = salaryItem.description || '';
  const isCeo = salaryType.includes('(대표자)');
  
  // 급여명 기본 부분 추출 (예: "센터장 급여" → "센터장", "사회복지사(대표자) 급여" → "사회복지사(대표자)")
  const baseName = salaryType.replace(' 급여', '');
  
  // 연결된 항목들의 description 업데이트 및 대표자 처리
  if (salaryItem.hb_pay_rel) {
    const payRelKey = salaryItem.hb_pay_rel;
    const linkedScodes = getLinkedScodes(salaryItem.fc_scode);
    
    if (isCeo && linkedScodes) {
      // 대표자인 경우: 퇴직금 행 삭제 (저장 시 실제 DB 반영)
      for (let i = processedData.value.length - 1; i >= 0; i--) {
        const item = processedData.value[i];
        if (item.hb_pay_rel === payRelKey && RETIREMENT_SCODES.includes(item.fc_scode)) {
          // 삭제 대기 목록에 추가
          if (item.hb_idx) {
            addToPendingDelete(item.hb_idx);
          }
          processedData.value.splice(i, 1);
        }
      }
    } else if (!isCeo && linkedScodes) {
      // 비대표자인 경우: 퇴직금 행이 없으면 생성
      const hasRetirement = processedData.value.some(
        item => item.hb_pay_rel === payRelKey && RETIREMENT_SCODES.includes(item.fc_scode)
      );
      
      if (!hasRetirement) {
        const retirementFinanceCode = findFinanceCodeByScode('OUT', linkedScodes.retirement);
        const retirementRow: ProcessedFinanceCode = {
          fc_idx: retirementFinanceCode?.fc_idx || 0,
          fc_trcode: 'OUT',
          fc_trname: '세출',
          fc_year: salaryItem.fc_year,
          fc_lcode: retirementFinanceCode?.fc_lcode || '',
          fc_lname: retirementFinanceCode?.fc_lname || '',
          fc_mcode: retirementFinanceCode?.fc_mcode || '',
          fc_mname: retirementFinanceCode?.fc_mname || '',
          fc_scode: linkedScodes.retirement,
          fc_sname: retirementFinanceCode?.fc_sname || '퇴직금',
          fc_funding: retirementFinanceCode?.fc_funding || '',
          fc_funding_name: retirementFinanceCode?.fc_funding_name || '',
          hb_idx: undefined,
          hb_pay_rel: payRelKey,
          hb_pay_rel_manual: 'N',
          hb_revenue_rel: undefined,
          budget_amount: "",
          description: `${baseName} 퇴직금`,
          unit_price: "",
          count: "",
          months: "",
          isAddedRow: true,
          isSalaryItem: false,
          isLinkedItem: true,
          isRevenuePairItem: false,
          showTrcode: false,
          showLcode: false,
          showMcode: false,
          showScode: false,
          trcodeRowspan: 0,
          lcodeRowspan: 0,
          mcodeRowspan: 0,
          scodeRowspan: 0,
        };
        const retirementScodeKey = `${retirementRow.fc_trcode}-${retirementRow.fc_lcode}-${retirementRow.fc_mcode}-${retirementRow.fc_scode}`;
        let retirementInsertIndex = -1;
        for (let i = 0; i < processedData.value.length; i++) {
          const row = processedData.value[i];
          const rowScodeKey = `${row.fc_trcode}-${row.fc_lcode}-${row.fc_mcode}-${row.fc_scode}`;
          if (rowScodeKey === retirementScodeKey) {
            retirementInsertIndex = i;
          }
        }
        if (retirementInsertIndex >= 0) {
          processedData.value.splice(retirementInsertIndex + 1, 0, retirementRow);
        }
      }
    }
    
    // 연결된 항목들의 description 업데이트
    processedData.value.forEach(item => {
      if (item.hb_pay_rel !== payRelKey) return;
      if (item.isSalaryItem) return; // 급여 자체는 제외
      
      const isInsurance = INSURANCE_SCODES.includes(item.fc_scode);
      const isRetirement = RETIREMENT_SCODES.includes(item.fc_scode);
      
      if (isInsurance) {
        item.description = `${baseName} 사회보험부담금`;
      } else if (isRetirement) {
        item.description = `${baseName} 퇴직금`;
      }
    });
    
    // rowspan 재계산 후 전체 목록에 반영
    recalculateRowspans();
    commitProcessedToAll();
    
    // 대표자 여부에 따라 재계산
    recalculateLinkedItems(salaryItem);
  }
};

// 대표자 여부 확인 헬퍼
const isCeoSalary = (item: ProcessedFinanceCode): boolean => {
  return !!item.isSalaryItem && (item.description || '').includes('(대표자)');
};

// 해당 행이 현재 포커스된 급여 그룹의 연결 항목인지 확인
const isLinkedHighlighted = (item: ProcessedFinanceCode): boolean => {
  if (!focusedPayRelGroup.value || !item.hb_pay_rel) return false;
  // 급여 항목이 아닌 연결된 항목만 하이라이트
  return item.hb_pay_rel === focusedPayRelGroup.value && !!item.isLinkedItem;
};

// 해당 행이 연결된 항목(수정 불가)인지 확인
// hb_pay_rel_manual='Y'이면 수동 편집 모드이므로 수정 가능
const isLinkedReadonly = (item: ProcessedFinanceCode): boolean => {
  return !!item.isLinkedItem && item.hb_pay_rel_manual !== 'Y';
};

/** 질의서: 대표와 센터장이 같으면 4대보험·퇴직적립금을 모두 뺀다 */
function isCenterOwnerPay(description: string | undefined): boolean {
  return (description || "").includes("센터장(대표자)");
}

function ceoInsuranceNotes(item: ProcessedFinanceCode): string[] {
  const salaryItem = processedData.value.find(
    (row) => row.hb_pay_rel === item.hb_pay_rel && SALARY_SCODES.includes(row.fc_scode)
  );
  if (isCenterOwnerPay(salaryItem?.description)) {
    return ["국민연금 제외", "건강보험·장기요양 제외", "고용보험 제외", "산재보험 제외", "퇴직적립금 제외"];
  }
  return ["고용보험 제외", "실업급여 제외", "산재보험 제외"];
}

// 해당 항목이 대표자 사회보험인지 확인 (툴팁 표시용)
const isLinkedCeoItem = (item: ProcessedFinanceCode): boolean => {
  if (!item.isLinkedItem || !item.hb_pay_rel) return false;
  // 사회보험 항목만 (퇴직금 제외)
  if (!INSURANCE_SCODES.includes(item.fc_scode)) return false;
  
  // 연결된 급여 항목 찾기
  const salaryItem = processedData.value.find(
    d => d.hb_pay_rel === item.hb_pay_rel && SALARY_SCODES.includes(d.fc_scode)
  );
  if (!salaryItem) return false;
  
  // 대표자 여부 확인
  return (salaryItem.description || '').includes('(대표자)');
};

// 잠금 해제 토글
const toggleManualEdit = (item: ProcessedFinanceCode) => {
  if (!item.isLinkedItem) return;
  
  if (item.hb_pay_rel_manual === 'Y') {
    // 수동 → 자동으로 전환: 현재 수동 입력 값을 저장
    item.manual_saved_unit_price = item.unit_price;
    item.manual_saved_count = item.count;
    item.manual_saved_months = item.months;
    item.hb_pay_rel_manual = 'N';
    
    // 자동계산 모드로 전환 시 연결된 급여 기반으로 재계산
    if (item.hb_pay_rel) {
      const salaryItem = processedData.value.find(
        row => row.hb_pay_rel === item.hb_pay_rel && row.isSalaryItem
      );
      if (salaryItem) {
        recalculateLinkedItems(salaryItem);
      }
    }
  } else {
    // 자동 → 수동으로 전환: 저장된 수동 입력 값이 있으면 복원
    item.hb_pay_rel_manual = 'Y';
    if (item.manual_saved_unit_price !== undefined) {
      item.unit_price = item.manual_saved_unit_price;
    }
    if (item.manual_saved_count !== undefined) {
      item.count = item.manual_saved_count;
    }
    if (item.manual_saved_months !== undefined) {
      item.months = item.manual_saved_months;
    }
  }
};

// 급여 변경 시 연결된 사회보험/퇴직금 자동 재계산
const recalculateLinkedItems = (salaryItem: ProcessedFinanceCode) => {
  if (!salaryItem.hb_pay_rel || !salaryItem.isSalaryItem) return;
  if (!effectiveTaxData.value) return;
  
  const payRelId = salaryItem.hb_pay_rel;
  
  // 대표자 여부 확인
  const isCeo = (salaryItem.description || '').includes('(대표자)');
  const centerOwner = isCenterOwnerPay(salaryItem.description);
  
  // 급여 정보 계산 (1인당 단가 기준)
  const unitPrice = Number(salaryItem.unit_price?.replace(/,/g, "") || 0);
  const count = Number(salaryItem.count?.replace(/,/g, "") || 0);
  const months = Number(salaryItem.months?.replace(/,/g, "") || 0);
  
  // 연결된 사회보험/퇴직금 항목 찾아서 재계산
  processedData.value.forEach(item => {
    if (item.hb_pay_rel !== payRelId) return;
    if (item.isSalaryItem) return; // 급여 자체는 제외
    
    // 수동 편집 모드('Y')인 항목은 자동 재계산 제외
    if (item.hb_pay_rel_manual === 'Y') return;
    
    const isInsurance = INSURANCE_SCODES.includes(item.fc_scode);
    const isRetirement = RETIREMENT_SCODES.includes(item.fc_scode);
    
    if (isInsurance && effectiveTaxData.value) {
      if (centerOwner) {
        item.unit_price = "0";
        item.count = count.toLocaleString();
        item.months = months.toLocaleString();
        return;
      }
      // 사회보험부담금 계산 (1인당 기준)
      const nationalPension = Math.round(unitPrice * (effectiveTaxData.value.tr_yeon || 0));
      const healthInsurance = Math.round(unitPrice * (effectiveTaxData.value.tr_gun || 0));
      const longTermCare = Math.round(healthInsurance * (effectiveTaxData.value.tr_jang || 0));
      
      // 대표자인 경우 고용보험, 실업급여, 산재보험 제외
      const employmentInsurance = isCeo ? 0 : Math.round(unitPrice * (effectiveTaxData.value.tr_go || 0));
      const unemployment = isCeo ? 0 : Math.round(unitPrice * (effectiveTaxData.value.tr_sil || 0));
      const industrialAccident = isCeo ? 0 : Math.round(unitPrice * (effectiveTaxData.value.tr_san || 0));
      
      // 1인당 월 사회보험부담금
      const perPersonInsurance = nationalPension + healthInsurance + longTermCare + 
                                 employmentInsurance + unemployment + industrialAccident;
      
      item.unit_price = perPersonInsurance.toLocaleString();
      item.count = count.toLocaleString(); // 급여의 명(회)와 동일
      item.months = months.toLocaleString(); // 급여의 개월과 동일
    } else if (isRetirement) {
      // 대표자인 경우 퇴직금 0원, 아니면 1인당 월 퇴직금 (급여의 1/12)
      const perPersonRetirement = isCeo ? 0 : Math.round(unitPrice / 12);
      
      item.unit_price = perPersonRetirement.toLocaleString();
      item.count = count.toLocaleString(); // 급여의 명(회)와 동일
      item.months = months.toLocaleString(); // 급여의 개월과 동일
    }
  });
};

// 급여 입력 변경 핸들러 (자동 재계산 트리거)
const handleSalaryInput = (event: any, item: ProcessedFinanceCode, field: string) => {
  // 기존 숫자 포맷팅 적용
  formatNumber(event, item, field);
  
  // 급여 항목이면 연결된 사회보험/퇴직금 재계산
  if (item.isSalaryItem && item.hb_pay_rel) {
    recalculateLinkedItems(item);
  }
};

watch(showDeleteDialog, async (show) => {
  if (!show) {
    deleteDialogBlocked.value = null;
    return;
  }
  deleteDialogChecking.value = true;
  deleteDialogBlocked.value = null;
  if (!hasMemberCode.value || !(props.bizGubun || "").trim()) {
    deleteDialogChecking.value = false;
    return;
  }
  try {
    const res = await budgetApi.getBudClasses({
      memberCode: memberCodeStr.value,
      year: searchParams.value.lebYear,
      bizGubun: props.bizGubun || "",
    });
    const raw = (res.data as { data?: string[] }).data;
    const list = Array.isArray(raw) && raw.length ? raw : [""];
    let maxN = 0;
    for (const id of list) {
      const s = id != null ? String(id) : "";
      if (!s || s === "0") continue;
      const n = parseInt(s, 10);
      if (!Number.isNaN(n) && String(n) === s) maxN = Math.max(maxN, n);
    }
    const cur = props.budgetClass != null ? String(props.budgetClass) : "";
    const curN =
      !cur || cur === "0"
        ? 0
        : (() => {
            const n = parseInt(cur, 10);
            return !Number.isNaN(n) && String(n) === cur ? n : -1;
          })();
    if (curN >= 0 && maxN > curN) {
      deleteDialogBlocked.value = `지금은 ${maxN}차 추경 데이터가 남아 있어, 먼저 그 탭에서 전체 삭제해야 합니다.`;
    }
  } catch {
    deleteDialogBlocked.value = null;
  } finally {
    deleteDialogChecking.value = false;
  }
});

// props.year 변경 시 동기화
watch(() => props.year, (newYear) => {
  if (newYear && newYear !== searchParams.value.lebYear) {
    searchParams.value.lebYear = newYear;
  }
}, { immediate: true });

// 세입/세출 탭: 서버 재조회 없이 표시만 전환 (리모컨 합계는 전체 유지)
watch(selectedTrcode, (_now, prev) => {
  if (suppressBudgetFetch.value) return;
  if (!allProcessedData.value.length && !processedData.value.length) return;
  // processedData는 아직 이전 탭 내용 → prev 기준으로 전체에 반영한 뒤 새 탭으로 표시
  commitProcessedToAll(prev ?? "ALL");
  refreshDisplayByTrcode();
});

// 연도·기관·사업구분·추경차수 변경 시 자동 조회 및 emit
watch(
  [
    () => searchParams.value.lebYear,
    () => props.memberCode,
    () => props.bizGubun,
    () => props.budgetClass,
  ],
  (now, prev) => {
    if (suppressBudgetFetch.value) return;
    const [year, member, biz, bud] = now;
    const prevYear = prev?.[0];
    const prevMember = prev?.[1];
    const prevBiz = prev?.[2];
    const prevBud = prev?.[3];
    if (prev && (year !== prevYear || member !== prevMember || biz !== prevBiz || bud !== prevBud)) {
      pendingDeleteIds.value = [];
    }
    if (year !== prevYear) {
      emit("year-change", searchParams.value.lebYear);
    }
    fetchFinanceCodes();
  }
);

onMounted(async () => {
  if (budgetLeaveSlot) {
    budgetLeaveSlot.value = { confirmDiscard, allowNextUnload };
  }
  window.addEventListener("beforeunload", onBudgetBeforeUnload);
  await fetchFinanceCodes();
});

onBeforeUnmount(() => {
  if (budgetLeaveSlot) budgetLeaveSlot.value = null;
  window.removeEventListener("beforeunload", onBudgetBeforeUnload);
});
</script>
